import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  ChevronUp, ChevronDown, Plus, Trash2, Save, FolderPlus, Layers,
  Clock, DollarSign, Users, Calendar, FileSpreadsheet, RefreshCw,
  Info,
} from "lucide-react";
import { getImplementationInputs, saveImplementationInputs, getRoiInputs, saveRoiInputs, implementationPlanXlsxUrl, listImplementationTemplates } from "../lib/adminApi";
import { useEffectiveRecommendations } from "./RecommendationOverride";
import { deriveProgrammeContext } from "./programmeContext";
import {
  cmStrategyFromTemplateKey,
  CM_STRATEGY_LABELS,
  CM_STRATEGY_DESCRIPTIONS,
  CM_STRATEGY_TRANSFORMATION_PATH,
} from "./cmStrategy";

/**
 * Consultant Workspace — Implementation Inputs (working sheet).
 *
 * Loads the seeded template (from /api/admin/assessments/{id}/implementation-inputs)
 * for the primary warehouse type and lets the consultant edit phases,
 * resources, hours/week, and start date.
 *
 * Does NOT run the scoring or ROI engines — calculations here are local
 * (phase_weeks × hours/week × rate) and persist as a standalone estimate.
 */

const DELIVERY_TYPES = [
  { value: "offshore", label: "Offshore" },
  { value: "onsite", label: "Onsite" },
];

const DEFAULT_NEW_HOURS = 40;

const money = (n, currency = "USD") => {
  const v = Number(n || 0);
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency", currency, maximumFractionDigits: 0,
    }).format(v);
  } catch {
    return `$${Math.round(v).toLocaleString()}`;
  }
};
const num = (n) => Math.round(Number(n || 0)).toLocaleString();

const newPid = () => (typeof crypto !== "undefined" && crypto.randomUUID)
  ? crypto.randomUUID()
  : `p-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

// -----------------------------------------------------------------------------
// Core calculation — pure functions over the working-sheet shape.
// Exported so the client Review Report can surface the same totals without
// duplicating math or pulling in editor UI.
// -----------------------------------------------------------------------------
export function computeCore(inputs) {
  const groups = inputs.phase_groups || [];
  const resources = inputs.resources || [];
  let totalHours = 0;
  let totalCost = 0;
  let totalWeeks = 0;

  for (const g of groups) {
    for (const p of g.phases || []) {
      const weeks = Number(p.duration_weeks) || 0;
      totalWeeks += weeks;
      for (const r of resources) {
        const hrsWk = Number((p.effort || {})[r.name]) || 0;
        const hours = hrsWk * weeks;
        const rate = r.delivery_type === "onsite"
          ? Number(r.onsite_rate) || 0
          : Number(r.offshore_rate) || 0;
        totalHours += hours;
        totalCost += hours * rate;
      }
    }
  }
  return { totalHours, totalCost, totalWeeks };
}

export function computeRollout() {
  // Rollout planning (additional-warehouse effort multiplier) has been removed
  // from the Implementation workflow. This no-op is retained only so any
  // older callers or snapshots don't crash — they'll just see zero rollout
  // figures, and the surrounding UI no longer surfaces them anyway.
  return { rolloutHours: 0, rolloutCost: 0 };
}

// -----------------------------------------------------------------------------
// Root component
// -----------------------------------------------------------------------------
export default function ImplementationInputsTab({ assessmentId, assessment, onSaved }) {
  const [inputs, setInputs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [seeded, setSeeded] = useState(false);

  // Effective (override-aware) programme recommendation context — the SAME
  // values shown in the Recommendations header. Display-only; engines never run.
  const { data: effectiveRecs } = useEffectiveRecommendations(assessmentId);
  const programme = useMemo(
    () => deriveProgrammeContext(assessment, effectiveRecs),
    [assessment, effectiveRecs],
  );

  // Currency + Exchange Rate + Discount come from the same assessment-level
  // ROI global inputs as the ROI tab — single source of truth. Editing here
  // goes back through the ROI save endpoint so values persist across tabs /
  // reloads and are picked up by the Executive Summary Report and the PDF.
  const [currency, setCurrency] = useState("USD");
  const [exchangeRate, setExchangeRate] = useState(1);
  const [discountPct, setDiscountPct] = useState(0);
  const [fxPending, setFxPending] = useState(0);
  const fxMountedRef = useRef(true);
  const fxLatestRef = useRef(null);
  const fxDirtyRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getImplementationInputs(assessmentId)
      .then((r) => {
        if (cancelled) return;
        setInputs(r.inputs);
        setSeeded(!!r.seeded);
      })
      .catch(() => { if (!cancelled) setMsg("Failed to load implementation inputs"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [assessmentId]);

  // Hydrate currency + exchange_rate + discount_pct from ROI global inputs
  // (same endpoint the ROI tab uses). Any edit on this tab saves back through
  // the same endpoint, so both tabs stay in sync automatically.
  useEffect(() => {
    let cancelled = false;
    getRoiInputs(assessmentId).then((r) => {
      if (cancelled) return;
      const c = r?.global_values?.currency || r?.values?.currency || "USD";
      const rate = Number(r?.global_values?.exchange_rate ?? r?.values?.exchange_rate);
      const disc = Number(r?.global_values?.discount_pct ?? r?.values?.discount_pct);
      setCurrency(String(c).toUpperCase());
      setExchangeRate(Number.isFinite(rate) && rate > 0 ? rate : 1);
      setDiscountPct(Number.isFinite(disc) && disc >= 0 ? disc : 0);
    }).catch(() => { /* keep defaults */ });
    return () => { cancelled = true; };
  }, [assessmentId]);

  // Mount/unmount guard + flush any unsaved FX edits to the backend if the
  // consultant switches tabs before the debounce fires.
  useEffect(() => {
    fxMountedRef.current = true;
    return () => {
      fxMountedRef.current = false;
      if (fxDirtyRef.current && fxLatestRef.current) {
        try { saveRoiInputs(assessmentId, fxLatestRef.current); } catch { /* no-op */ }
        fxDirtyRef.current = false;
        fxLatestRef.current = null;
      }
    };
  }, [assessmentId]);

  // Debounced save for FX/discount edits (mirrors the ROI tab's pattern).
  useEffect(() => {
    if (fxPending === 0) return undefined;
    const payload = {
      global_inputs: {
        currency,
        exchange_rate: Number(exchangeRate) || 1,
        discount_pct: Math.max(0, Number(discountPct) || 0),
      },
    };
    fxLatestRef.current = payload;
    fxDirtyRef.current = true;
    const handle = setTimeout(async () => {
      try {
        await saveRoiInputs(assessmentId, payload);
        fxDirtyRef.current = false;
        fxLatestRef.current = null;
        if (fxMountedRef.current) flash("Pricing saved");
      } catch {
        if (fxMountedRef.current) flash("Pricing save failed");
      }
    }, 500);
    return () => clearTimeout(handle);
  }, [fxPending]); // eslint-disable-line react-hooks/exhaustive-deps

  // Page-refresh / close guard — use fetch keepalive so the PUT survives.
  useEffect(() => {
    const onUnload = () => {
      if (!fxDirtyRef.current || !fxLatestRef.current) return;
      try {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/assessments/${assessmentId}/roi-inputs`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          keepalive: true,
          body: JSON.stringify(fxLatestRef.current),
        });
        fxDirtyRef.current = false;
      } catch { /* no-op */ }
    };
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
  }, [assessmentId]);

  const touchFx = () => setFxPending((n) => n + 1);
  const updateCurrency = (v) => { setCurrency(String(v || "USD").toUpperCase()); touchFx(); };
  const updateExchangeRate = (v) => {
    const n = Number(v);
    setExchangeRate(Number.isFinite(n) && n > 0 ? n : v === "" ? 1 : exchangeRate);
    touchFx();
  };
  const updateDiscountPct = (v) => {
    if (v === "" || v == null) { setDiscountPct(0); touchFx(); return; }
    const n = Number(v);
    if (!Number.isFinite(n)) return;
    setDiscountPct(Math.max(0, n));
    touchFx();
  };

  const update = (patch) => setInputs((prev) => ({ ...prev, ...patch }));

  const isCloudMigration = (assessment?.scenario_type || "").toLowerCase() === "cloud_migration";

  // Cloud Migration: load the admin-managed SmartEvaluate / cloud_migration
  // templates for the selectable cards (never hardcoded).
  const [cmTemplates, setCmTemplates] = useState([]);
  useEffect(() => {
    if (!isCloudMigration) return;
    let cancelled = false;
    listImplementationTemplates()
      .then((all) => {
        if (cancelled) return;
        const list = (all || []).filter((t) =>
          (t.application || "") === "SmartEvaluate" &&
          (t.evaluation_type || "") === "cloud_migration" &&
          t.active !== false
        ).sort((a, b) => (a.display_order ?? 99) - (b.display_order ?? 99));
        setCmTemplates(list);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isCloudMigration]);

  const flash = (text) => { setMsg(text); setTimeout(() => setMsg(""), 2500); };

  // Cloud Migration transformation strategy (derived from the selected /
  // approved template key). Drives the read-only Step 1 label + which
  // implementation templates are offered (Part 3).
  const cmStrategyKey = cmStrategyFromTemplateKey((inputs?.rollout_mode) || "cm_lift_shift");
  const cmFilteredTemplates = useMemo(() => {
    const path = CM_STRATEGY_TRANSFORMATION_PATH[cmStrategyKey];
    return (cmTemplates || []).filter(
      (t) => (t.transformation_path || "technical_migration") === path,
    );
  }, [cmTemplates, cmStrategyKey]);

  const handleSave = async ({ regenerateWaves = false, regeneratePhaseGroups = false, mode = null } = {}) => {
    if (!inputs) return;
    setSaving(true);
    try {
      let payload = inputs;
      if (mode) payload = { ...payload, rollout_mode: mode };
      if (regenerateWaves) payload = { ...payload, regenerate_waves: true };
      if (regeneratePhaseGroups) payload = { ...payload, regenerate_phase_groups: true };
      const r = await saveImplementationInputs(assessmentId, payload);
      setInputs(r.inputs);
      setSeeded(false);
      flash(
        regeneratePhaseGroups ? "Phase Groups regenerated" :
        regenerateWaves ? "Waves regenerated" : "Saved"
      );
      // iter 67: bubble save to the parent so it can refresh readiness +
      // the blocker banner immediately (otherwise they stay stale until
      // a full page reload).
      if (typeof onSaved === "function") onSaved();
    } catch (e) {
      flash(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerateWaves = () => handleSave({ regenerateWaves: true });
  const handleRegeneratePhaseGroups = (mode = null) =>
    handleSave({ regeneratePhaseGroups: true, mode });

  const core = useMemo(() => inputs ? computeCore(inputs) : { totalHours: 0, totalCost: 0, totalWeeks: 0 }, [inputs]);

  if (loading) return <p className="text-xs text-[#9CA3AF]">Loading implementation inputs…</p>;
  if (!inputs) return <p className="text-xs text-red-500">{msg || "No data"}</p>;

  return (
    <div className="space-y-5 max-w-[1200px] mx-auto w-full" data-testid="impl-inputs-tab">
      {seeded && (
        <div className="px-3 py-2 bg-[#FFF7ED] border border-[#FDBA74]/40 rounded-sm text-xs text-[#9A3412]">
          This working sheet was just seeded from the admin template for{" "}
          <strong>{inputs.primary_solution_label || "the primary solution"}</strong>.
          Review and click <strong>Save</strong> to persist your edits.
        </div>
      )}
      {msg && !seeded && (
        <div data-testid="impl-inputs-msg" className="px-3 py-2 bg-[#E0F2FE] border border-[#025979]/20 rounded-sm text-xs text-[#025979]">{msg}</div>
      )}

      {isCloudMigration ? (
        <CloudMigrationTemplateSelector
          templates={cmFilteredTemplates}
          strategyKey={cmStrategyKey}
          inputs={inputs}
          seeded={seeded}
          totalWeeks={core.totalWeeks}
          saving={saving}
          onSelectTemplate={(mode) => handleRegeneratePhaseGroups(mode)}
        />
      ) : (
        <>
          <WarehouseNetworkSummary
            inputs={inputs}
            isGrouped={(assessment?.warehouse_groups || []).length > 0}
            sitesInScope={inputs.sites_in_scope || {}}
            onUpdate={update}
          />

          <ImplementationStrategySection
            inputs={inputs}
            programme={programme}
            isGrouped={(assessment?.warehouse_groups || []).length > 0}
            onUpdate={update}
            onRegeneratePhaseGroups={handleRegeneratePhaseGroups}
            saving={saving}
          />
        </>
      )}
      <StartDateRow
        startDate={inputs.start_date || ""}
        onStartDate={(v) => update({ start_date: v })}
      />

      {/* Top Summary — mirrors ROI tab structure (summary at top, inputs below). */}
      <InvestmentSummarySection
        core={core}
        costBreakdown={inputs.cost_breakdown}
        currency={currency}
        exchangeRate={exchangeRate}
        discountPct={discountPct}
        onCurrency={updateCurrency}
        onExchangeRate={updateExchangeRate}
        onDiscount={updateDiscountPct}
      />

      <PhaseGroupsEditor
        inputs={inputs}
        groups={inputs.phase_groups || []}
        resources={inputs.resources || []}
        onChange={(next) => update({ phase_groups: next })}
        onMessage={flash}
        isCloudMigration={isCloudMigration}
      />

      <ResourcesEditor
        resources={inputs.resources || []}
        groups={inputs.phase_groups || []}
        onChange={(next, nextGroups) => {
          if (nextGroups) update({ resources: next, phase_groups: nextGroups });
          else update({ resources: next });
        }}
      />

      <EffortGrid
        groups={inputs.phase_groups || []}
        resources={inputs.resources || []}
        onUpdateCell={(gi, pi, resourceName, raw) => {
          setInputs((prev) => {
            const groups = (prev.phase_groups || []).map((g, i) => {
              if (i !== gi) return g;
              const phases = (g.phases || []).map((p, j) => {
                if (j !== pi) return p;
                const effort = { ...(p.effort || {}) };
                const n = raw === "" ? 0 : Number(raw);
                effort[resourceName] = Number.isFinite(n) ? n : 0;
                return { ...p, effort };
              });
              return { ...g, phases };
            });
            return { ...prev, phase_groups: groups };
          });
        }}
      />

      <CostModelSection
        costBreakdown={inputs.cost_breakdown}
        computedLaborCost={core.totalCost}
        onUpdate={(nextCb) => update({ cost_breakdown: nextCb })}
      />

      <div className="flex items-center justify-end gap-2 pt-2">
        <a
          data-testid="impl-export-excel"
          href={implementationPlanXlsxUrl(assessmentId)}
          target="_blank"
          rel="noopener noreferrer"
          download
          className="inline-flex items-center text-xs bg-white border border-[#025979] text-[#025979] hover:bg-[#E6F1F5] rounded-sm px-3 py-1.5"
          title="Right-click and choose 'Save link as…' if the browser blocks the download."
        >
          <FileSpreadsheet className="w-3 h-3 mr-1.5" /> Export to Excel (right click)
        </a>
        <Button
          data-testid="impl-inputs-save"
          onClick={() => handleSave()}
          disabled={saving}
          className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"
        >
          <Save className="w-3 h-3 mr-1.5" /> {saving ? "Saving…" : "Save implementation inputs"}
        </Button>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Top context strip
// -----------------------------------------------------------------------------
// -----------------------------------------------------------------------------
// Warehouse network summary (read-only, sourced from the assessment)
// -----------------------------------------------------------------------------
// Cloud Migration template selector — admin-managed cards + change warning.
const CM_TEMPLATE_DESCRIPTIONS = {
  cm_lift_shift: "Technical migration with minimal functional change — fastest path to the target platform.",
  cm_lift_shift_validate: "Technical migration plus full functional, integration and regression validation before go-live.",
  selective_migration: "Business improvements combined with technical migration while maintaining controlled scope.",
  full_transformation: "End-to-end business and technology transformation including process redesign and modernization.",
};

function CloudMigrationTemplateSelector({ templates, strategyKey, inputs, seeded, totalWeeks, saving, onSelectTemplate }) {
  const [pending, setPending] = useState(null); // solution_type awaiting confirm
  const selected = inputs.rollout_mode || "";
  const planExists = !seeded; // a saved plan already exists

  const choose = (st) => {
    if (st === selected) return;
    if (planExists) { setPending(st); return; }
    onSelectTemplate(st);
  };

  return (
    <>
      {/* Step 1 — Selected Transformation Strategy (read-only) */}
      <section data-testid="cm-selected-strategy" className="bg-white border border-[#E5E7EB] rounded-sm px-5 py-4 border-l-2 border-l-[#025979]">
        <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF] font-semibold">Selected Transformation Strategy</p>
        <p data-testid="cm-selected-strategy-name" className="text-[15px] font-bold text-[#0F172A] mt-0.5" style={{ fontFamily: "Chivo, sans-serif" }}>
          {CM_STRATEGY_LABELS[strategyKey]}
        </p>
        <p className="text-[11.5px] text-[#6B7280] mt-0.5 leading-relaxed">{CM_STRATEGY_DESCRIPTIONS[strategyKey]}</p>
      </section>

      <section data-testid="cm-template-selector" className="bg-white border border-[#E5E7EB] rounded-sm px-5 py-5">
      <div className="mb-3">
        <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold" style={{ fontFamily: "Chivo, sans-serif" }}>
          Implementation Template
        </h3>
        <p className="text-[11px] text-[#6B7280] mt-0.5">
          Select the Cloud Migration implementation template. The recommended template is pre-selected from the approved transformation strategy.
        </p>
      </div>

      {templates.length === 0 ? (
        <p className="text-[12px] text-[#9CA3AF] italic">No Cloud Migration templates configured.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {templates.map((t) => {
            const st = t.solution_type;
            const isSel = st === selected;
            const groups = t.phase_groups || [];
            const phases = groups.reduce((n, g) => n + (g.phases || []).length, 0);
            const desc = CM_TEMPLATE_DESCRIPTIONS[st] || t.solution_label || "";
            return (
              <button
                type="button"
                key={st}
                data-testid={`cm-template-card-${st}`}
                onClick={() => choose(st)}
                disabled={saving}
                className={`text-left rounded-sm border p-4 transition-colors ${
                  isSel
                    ? "border-[#025979] bg-[#E0F2FE] ring-1 ring-[#025979]"
                    : "border-[#E5E7EB] bg-white hover:border-[#025979]/40"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-[9.5px] uppercase tracking-wider text-[#9CA3AF]">{t.template_category || "Migration Strategy"}</p>
                    <h4 className="text-sm font-bold text-[#0F172A]" style={{ fontFamily: "Chivo, sans-serif" }}>{t.template_name}</h4>
                  </div>
                  {isSel && (
                    <span data-testid={`cm-template-selected-${st}`} className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-[#025979] bg-white border border-[#025979]/30 rounded-sm px-1.5 py-0.5">Selected</span>
                  )}
                </div>
                {t.indicative_duration && (
                  <div className="mt-1.5">
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#025979]">
                      <Clock className="w-3 h-3" /> {t.indicative_duration}
                    </span>
                  </div>
                )}
                <p className="mt-2 text-[11.5px] text-[#475569] leading-relaxed">{desc}</p>
                <div className="mt-3 flex flex-wrap gap-1.5 text-[10px] text-[#6B7280]">
                  <span className="inline-flex items-center gap-1 border border-[#E5E7EB] rounded-sm px-1.5 py-0.5"><FolderPlus className="w-3 h-3" /> {groups.length} groups</span>
                  <span className="inline-flex items-center gap-1 border border-[#E5E7EB] rounded-sm px-1.5 py-0.5"><Layers className="w-3 h-3" /> {phases} phases</span>
                  {(t.resources || []).length > 0 && (
                    <span className="inline-flex items-center gap-1 border border-[#E5E7EB] rounded-sm px-1.5 py-0.5"><Users className="w-3 h-3" /> {(t.resources || []).length} resources</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Duration display: indicative (admin) + generated plan weeks */}
      {selected && (
        <div data-testid="cm-duration-display" className="mt-4 flex flex-wrap gap-x-8 gap-y-1 text-[12px]">
          {inputs.indicative_duration && (
            <span className="text-[#374151]"><span className="text-[#6B7280]">Indicative Duration:</span> <strong>{inputs.indicative_duration}</strong></span>
          )}
          {totalWeeks > 0 && (
            <span className="text-[#374151]"><span className="text-[#6B7280]">Generated Plan:</span> <strong>{Math.round(totalWeeks)} Weeks</strong></span>
          )}
        </div>
      )}

      {pending && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="cm-template-change-modal">
          <div className="bg-white rounded-sm border border-[#E5E7EB] shadow-xl max-w-md w-full mx-4 p-5">
            <h4 className="text-sm font-bold text-[#0F172A] mb-2">Change implementation template?</h4>
            <p className="text-[12.5px] text-[#475569] leading-relaxed">
              Changing the implementation template will regenerate the implementation stages and may replace unsaved implementation edits. Continue?
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" className="rounded-sm text-xs" data-testid="cm-template-change-cancel" onClick={() => setPending(null)}>Cancel</Button>
              <Button className="rounded-sm text-xs bg-[#025979] hover:bg-[#03738C] text-white" data-testid="cm-template-change-confirm"
                onClick={() => { const st = pending; setPending(null); onSelectTemplate(st); }}>
                Continue
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
    </>
  );
}


function WarehouseNetworkSummary({ inputs, isGrouped = false, sitesInScope = {}, onUpdate }) {
  const unitNoun = isGrouped ? "warehouse group" : "warehouse type";
  const unitCol = isGrouped ? "Warehouse Group" : "Warehouse Type";
  // iter 61: default complexity fallback by warehouse-type key. Backend
  // already fills `primary_complexity_label` / `complexity_label`, but we keep
  // this defensive map so the Overview never renders blank on stale client
  // state either.
  const DEFAULT_CX_BY_TYPE = {
    cdc: "High",
    manufacturing: "High",
    rdc: "Medium",
    ecommerce: "Medium",
    spare_parts: "Medium",
    satellite: "Low",
  };
  const cxFallback = (t, label) => {
    if (label && String(label).trim() && label !== "—") return label;
    return DEFAULT_CX_BY_TYPE[t] || "Medium";
  };
  const primaryLabel = inputs.primary_warehouse_type_label || "—";
  const primaryCount = Number(inputs.primary_warehouse_count) || 0;
  const primaryCx = cxFallback(inputs.primary_warehouse_type, inputs.primary_complexity_label);
  const rollout = Array.isArray(inputs.rollout) ? inputs.rollout : [];
  const rows = [];
  if (primaryLabel !== "—" || primaryCount > 0) {
    rows.push({
      key: "__primary__",
      groupId: inputs.primary_warehouse_type || "",
      label: primaryLabel,
      count: primaryCount,
      complexity: primaryCx,
    });
  }
  rollout.forEach((r) => {
    rows.push({
      key: r.warehouse_type || r.warehouse_type_label,
      groupId: r.warehouse_type || "",
      label: r.warehouse_type_label || r.warehouse_type || "—",
      count: Number(r.warehouse_count) || 0,
      complexity: cxFallback(r.warehouse_type, r.complexity_label),
    });
  });
  const totalTypes = rows.length;
  const totalLocations = rows.reduce((n, r) => n + (r.count || 0), 0)
    || Number(inputs.total_warehouses) || 0;

  return (
    <section
      data-testid="impl-warehouse-network"
      className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm"
    >
      <div className="px-4 py-3 border-b border-[#E5E7EB]">
        <h3 className="text-sm font-semibold text-[#111827] flex items-center gap-1.5" style={{ fontFamily: "Chivo" }}>
          <Layers className="w-3.5 h-3.5 text-[#025979]" />
          Warehouse network overview
        </h3>
        <p className="text-[11px] text-[#6B7280] mt-0.5" data-testid="impl-wh-network-summary">
          {totalTypes} {unitNoun}{totalTypes === 1 ? "" : "s"} across {totalLocations} site{totalLocations === 1 ? "" : "s"}
        </p>
      </div>
      <div className="p-4">
        {rows.length === 0 ? (
          <p className="text-xs text-[#9CA3AF] italic">No warehouse data available.</p>
        ) : (
          <table className="min-w-full text-xs border-collapse">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">
                <th className="text-left px-3 py-2 border-b border-[#E5E7EB]">{unitCol}</th>
                <th className="text-right px-3 py-2 border-b border-[#E5E7EB] w-32"># Sites</th>
                <th className="text-left px-3 py-2 border-b border-[#E5E7EB] w-40">Complexity</th>
                <th className="text-right px-3 py-2 border-b border-[#E5E7EB] w-40">Sites in Scope</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => {
                const scopeVal = (r.groupId && (sitesInScope[r.groupId] ?? r.count)) ?? r.count;
                return (
                <tr key={r.key || idx} data-testid={`impl-wh-row-${idx}`}>
                  <td className="px-3 py-2 border-b border-[#E5E7EB] text-[#111827] font-medium">{r.label}</td>
                  <td className="px-3 py-2 border-b border-[#E5E7EB] text-right text-[#111827] font-medium">{r.count}</td>
                  <td className="px-3 py-2 border-b border-[#E5E7EB] text-[#374151]">{r.complexity}</td>
                  <td className="px-3 py-2 border-b border-[#E5E7EB] text-right">
                    {r.groupId ? (
                      <span className="inline-flex items-center gap-1 justify-end">
                        <input
                          type="number"
                          min="0"
                          max={r.count}
                          step="1"
                          value={scopeVal}
                          data-testid={`impl-wh-scope-input-${r.groupId}`}
                          onChange={(e) => {
                            const raw = e.target.value;
                            if (raw === "") return;
                            let n = parseInt(raw, 10);
                            if (Number.isNaN(n)) return;
                            n = Math.max(0, Math.min(n, r.count));
                            if (typeof onUpdate !== "function") return;
                            const prev = sitesInScope[r.groupId] ?? r.count;
                            const apply = (val) => onUpdate({ sites_in_scope: { ...sitesInScope, [r.groupId]: val } });
                            apply(n);
                            // Warn (with undo) when setting scope to 0 will drop
                            // this group's existing rollout-stage assignments on save.
                            if (n === 0 && Number(prev) > 0) {
                              const FOUNDATION_RE = /foundation|discover|blueprint|prepare/i;
                              const stages = (inputs.phase_groups || []).filter(
                                (g) => !FOUNDATION_RE.test(String(g.name || "")) &&
                                  (g.scope_assignments || []).some(
                                    (a) => a.warehouse_type === r.groupId && Number(a.location_count) > 0)
                              );
                              if (stages.length) {
                                toast.warning(
                                  `${r.label}: saving 0 sites in scope will remove it from ${stages.length} rollout stage${stages.length > 1 ? "s" : ""} — ${stages.map((g) => g.name).join(", ")}.`,
                                  { duration: 8000, action: { label: "Undo", onClick: () => apply(prev) } }
                                );
                              }
                            }
                          }}
                          className="w-14 text-xs text-right px-1.5 py-1 rounded-sm border border-[#86EFAC] bg-[#F0FDF4] text-[#111827] focus:outline-none focus:ring-1 focus:ring-[#025979]"
                        />
                        <span className="text-[10px] text-[#9CA3AF]">of {r.count}</span>
                      </span>
                    ) : (
                      <span className="text-[10px] text-[#9CA3AF]">—</span>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Project start date — moved out of the top strip to sit just above the
// Phase Groups section so the consultant picks the date right before
// scheduling phases.
// -----------------------------------------------------------------------------
function StartDateRow({ startDate, onStartDate }) {
  return (
    <section
      data-testid="impl-start-date-row"
      className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm p-4 flex items-center gap-4"
    >
      <label htmlFor="impl-start-date" className="text-[11px] uppercase tracking-wider text-[#9CA3AF]">
        Project Start Date
      </label>
      <Input
        id="impl-start-date"
        data-testid="impl-start-date"
        type="date"
        value={startDate}
        onChange={(e) => onStartDate(e.target.value)}
        className="rounded-sm text-xs h-8 w-48"
      />
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-wider text-[#9CA3AF] mb-1">{label}</div>
      {children}
    </label>
  );
}

// -----------------------------------------------------------------------------
// Live summary panel
// -----------------------------------------------------------------------------
function LiveSummary({ core, currency = "USD", exchangeRate = 1, discountPct = 0 }) {
  const totalHours = core.totalHours;
  const baseTotalCost = core.totalCost;
  const months = core.totalWeeks > 0 ? (core.totalWeeks / 4.345).toFixed(1) : "0";
  // Avg team size: full-time equivalents across the core timeline.
  const avgTeam = core.totalWeeks > 0
    ? (core.totalHours / (core.totalWeeks * 40)).toFixed(1)
    : "0";
  const discountFactor = 1 - Math.max(0, Number(discountPct) || 0) / 100;
  const convertedTotal = (Number(baseTotalCost) || 0) * exchangeRate;
  const finalTotal = convertedTotal * discountFactor;
  const hasDiscount = Math.abs(1 - discountFactor) > 1e-9;

  return (
    <section data-testid="impl-summary" className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <SummaryTile icon={<Calendar className="w-3.5 h-3.5" />} label="Total duration"
        primary={`${core.totalWeeks} wks`} secondary={`${months} months`} testid="sum-duration" />
      <SummaryTile icon={<Clock className="w-3.5 h-3.5" />} label="Total effort"
        primary={`${num(totalHours)} hrs`}
        secondary="Across all phases"
        testid="sum-effort" />
      <SummaryTile icon={<DollarSign className="w-3.5 h-3.5" />} label="Total cost"
        primary={money(finalTotal, currency)}
        secondary={hasDiscount
          ? `${money(convertedTotal, currency)} · ${Number(discountPct)}% discount applied`
          : "Across all phases"}
        testid="sum-cost" />
      <SummaryTile icon={<Users className="w-3.5 h-3.5" />} label="Avg team size"
        primary={`${avgTeam} FTE`} secondary="Full-time equivalents" testid="sum-team" />
    </section>
  );
}

// -----------------------------------------------------------------------------
// Currency & Exchange Rate — shares the same assessment-level ROI global
// inputs (single source of truth). Edits debounce-save through saveRoiInputs
// just like the ROI tab, so the Review Report / PDF / email all see the same
// values without any separate persistence path.
// -----------------------------------------------------------------------------
function CurrencySection({ currency, exchangeRate, discountPct, onCurrency, onExchangeRate, onDiscount }) {
  return (
    <section
      data-testid="impl-currency-section"
      className="bg-white border border-[#E5E7EB] rounded-sm p-5"
    >
      <h4 className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-wider mb-3">
        Currency, Exchange Rate &amp; Discount
      </h4>
      <p className="text-[10.5px] text-[#9CA3AF] mb-3">
        Shared with the ROI tab. Discount only reduces the Implementation Total
        cost shown here and in the Executive Summary Report — ROI figures are
        unaffected.
      </p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Field label="Currency">
          <Input
            data-testid="impl-currency-input"
            type="text"
            value={currency}
            placeholder="USD"
            onChange={(e) => onCurrency(e.target.value)}
            className="rounded-sm text-sm h-8"
          />
        </Field>
        <Field label="Exchange Rate">
          <Input
            data-testid="impl-exchange-rate-input"
            type="number"
            step="0.0001"
            value={exchangeRate}
            onChange={(e) => onExchangeRate(e.target.value)}
            className="rounded-sm text-sm h-8"
          />
        </Field>
        <Field label="Discount %">
          <Input
            data-testid="impl-discount-input"
            type="number"
            step="0.1"
            min="0"
            value={discountPct}
            onChange={(e) => onDiscount(e.target.value)}
            className="rounded-sm text-sm h-8"
          />
        </Field>
      </div>
    </section>
  );
}

function SummaryTile({ icon, label, primary, secondary, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-[#E5E7EB] rounded-sm p-3 shadow-sm">
      <div className="text-[10px] uppercase tracking-wider text-[#9CA3AF] flex items-center gap-1 mb-1">
        {icon}{label}
      </div>
      <div className="text-lg font-bold text-[#111827] leading-tight">{primary}</div>
      <div className="text-[10px] text-[#6B7280] mt-0.5">{secondary}</div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Phase Groups / Phases editor
// -----------------------------------------------------------------------------
function PhaseGroupsEditor({ inputs, groups, resources, onChange, onMessage, isCloudMigration = false }) {
  const reindex = (arr) => arr.map((x, i) => ({ ...x, order: i }));
  const [expanded, setExpanded] = useState({}); // default collapsed

  // --- Warehouse types + per-type totals (from inputs.rollout + primary) ---
  const allTypes = (() => {
    const m = new Map();
    if (inputs?.primary_warehouse_type) {
      m.set(inputs.primary_warehouse_type, {
        key: inputs.primary_warehouse_type,
        label: inputs.primary_warehouse_type_label || inputs.primary_warehouse_type,
        count: Number(inputs.primary_warehouse_count || 0) || 1,
      });
    }
    for (const r of inputs?.rollout || []) {
      if (!r.warehouse_type || m.has(r.warehouse_type)) continue;
      m.set(r.warehouse_type, {
        key: r.warehouse_type,
        label: r.warehouse_type_label || r.warehouse_type,
        count: Number(r.warehouse_count || 0) || 1,
      });
    }
    return Array.from(m.values());
  })();
  const typeTotal = (k) => (allTypes.find((t) => t.key === k) || {}).count || 0;
  const typeLabel = (k) => (allTypes.find((t) => t.key === k) || {}).label || k;

  // Effective implementation scope per group (group_uuid -> sites_in_scope);
  // defaults to the group's total # sites when never saved. Rollout stage
  // assignments are capped at this; Foundation stays program-wide (total).
  const scopeCap = (k) => {
    const s = inputs?.sites_in_scope;
    const v = s ? s[k] : undefined;
    if (v === 0) return 0;
    const n = Number(v);
    return Number.isFinite(n) && n >= 0 ? Math.min(n, typeTotal(k)) : typeTotal(k);
  };

  const FOUNDATION_RE = /foundation|discover|blueprint|prepare/i;
  const POST_RE = /hypercare|stabili[sz]e|stabili[sz]ation/i;
  const isFoundation = (g) => FOUNDATION_RE.test(g?.name || "");
  const isPost = (g) => POST_RE.test(g?.name || "");
  const isRollout = (g) => !isFoundation(g) && !isPost(g);
  const stageIsFoundation = (gi) => isFoundation(groups[gi]);
  const stageCap = (gi, key) => (stageIsFoundation(gi) ? typeTotal(key) : scopeCap(key));

  // --- Per-group derived data ---
  const pgDuration = (g) => (g.phases || []).reduce((s, p) => s + Number(p.duration_weeks || 0), 0);
  const preWeeks = groups.reduce((s, g) => s + (isFoundation(g) ? pgDuration(g) : 0), 0);
  // Sequential timing: each rollout group chains after the previous (Foundation offsets)
  const timingByIndex = (() => {
    const out = {};
    let cursor = preWeeks;
    groups.forEach((g, i) => {
      if (isRollout(g)) {
        const dur = pgDuration(g);
        out[i] = { start: Math.round(cursor), end: Math.round(cursor + dur), goLiveMonth: Math.max(1, Math.ceil((cursor + dur) / 4.345)) };
        cursor += dur;
      } else if (isFoundation(g)) {
        const dur = pgDuration(g);
        out[i] = { start: 0, end: Math.round(dur), goLiveMonth: null };
      } else {
        // post-rollout (hypercare): append after
        const dur = pgDuration(g);
        out[i] = { start: Math.round(cursor), end: Math.round(cursor + dur), goLiveMonth: null };
      }
    });
    return out;
  })();

  // --- Program-wide assigned totals per type (across all groups) ---
  const assignedByType = (() => {
    const m = {};
    groups.forEach((g) => {
      for (const a of g.scope_assignments || []) {
        m[a.warehouse_type] = (m[a.warehouse_type] || 0) + (Number(a.location_count) || 0);
      }
    });
    return m;
  })();

  const addGroup = () => onChange([
    ...groups,
    { name: `Stage ${groups.length + 1}`, order: groups.length, warehouse_count: null, phases: [], scope_assignments: [] },
  ]);
  const moveGroup = (gi, dir) => {
    const next = [...groups];
    const t = gi + dir;
    if (t < 0 || t >= next.length) return;
    [next[gi], next[t]] = [next[t], next[gi]];
    onChange(reindex(next));
  };
  const removeGroup = (gi) => onChange(reindex(groups.filter((_, i) => i !== gi)));
  const updateGroup = (gi, patch) => onChange(groups.map((g, i) => i === gi ? { ...g, ...patch } : g));

  // --- Assignment operations on a group ---
  const getAssignments = (g) => Array.isArray(g.scope_assignments) ? g.scope_assignments : [];
  const addAssignment = (gi, key) => {
    // iter 59: soft model — same warehouse type may appear in multiple stages.
    // No clamp; default to full network count so consultants don't have to
    // dial it up every time they assign to a common stage like Foundation.
    const g = groups[gi];
    const existing = getAssignments(g);
    if (existing.find((a) => a.warehouse_type === key)) return;
    const suggested = stageCap(gi, key) || (stageIsFoundation(gi) ? 1 : 0);
    updateGroup(gi, { scope_assignments: [...existing, { warehouse_type: key, location_count: suggested }] });
  };
  const removeAssignment = (gi, key) => {
    const g = groups[gi];
    updateGroup(gi, { scope_assignments: getAssignments(g).filter((a) => a.warehouse_type !== key) });
  };
  const changeAssignmentCount = (gi, key, raw) => {
    // iter 59: soft model — allow any positive value up to the network total
    // for the type. Sum-across-stages may legitimately exceed the total
    // because Foundation/common stages aren't mutually exclusive with rollout.
    const g = groups[gi];
    const cap = stageCap(gi, key);
    const want = Math.max(0, Math.floor(Number(raw) || 0));
    const finalCount = cap > 0 ? Math.min(want, cap) : 0;
    const next = getAssignments(g)
      .map((a) => a.warehouse_type === key ? { ...a, location_count: finalCount } : a)
      .filter((a) => Number(a.location_count) > 0);
    updateGroup(gi, { scope_assignments: next });
  };

  const addPhase = (gi) => onChange(groups.map((g, i) => {
    if (i !== gi) return g;
    // New phases default to 40 hrs/week for every existing resource.
    const effort = {};
    for (const r of resources) effort[r.name] = DEFAULT_NEW_HOURS;
    const phases = [...(g.phases || []), {
      id: newPid(),
      name: "New phase",
      duration_weeks: 1,
      order: (g.phases || []).length,
      effort,
    }];
    return { ...g, phases };
  }));
  const removePhase = (gi, pi) => onChange(groups.map((g, i) => {
    if (i !== gi) return g;
    return { ...g, phases: (g.phases || []).filter((_, j) => j !== pi).map((p, j) => ({ ...p, order: j })) };
  }));
  const updatePhase = (gi, pi, patch) => onChange(groups.map((g, i) => {
    if (i !== gi) return g;
    return { ...g, phases: (g.phases || []).map((p, j) => j === pi ? { ...p, ...patch } : p) };
  }));
  const movePhase = (gi, pi, dir) => onChange(groups.map((g, i) => {
    if (i !== gi) return g;
    const phases = [...(g.phases || [])];
    const t = pi + dir;
    if (t < 0 || t >= phases.length) return g;
    [phases[pi], phases[t]] = [phases[t], phases[pi]];
    phases.forEach((p, k) => { p.order = k; });
    return { ...g, phases };
  }));

  return (
    <SectionCard
      title="Implementation Stages"
      description="Define implementation stages, phases, weeks, and warehouse scope. ROI and timeline are derived from this structure."
      action={
        <Button data-testid="impl-add-group" onClick={addGroup} variant="outline" className="rounded-sm text-xs">
          <FolderPlus className="w-3 h-3 mr-1" /> Add stage
        </Button>
      }
    >
      {!isCloudMigration && (
        <div
          className="text-[11px] text-[#6B7280] mb-2 px-3 py-2 rounded-sm border border-[#E5E7EB] bg-[#F8FAFC]"
          data-testid="impl-rollout-coverage-guidance"
        >
          <strong>Rollout Coverage Guidance:</strong> Foundation can apply to all warehouses. Pilot and rollout stages should cover the implementation rollout scope. The same warehouse type can appear in multiple stages — Foundation doesn't consume rollout coverage.
        </div>
      )}
      <div className="space-y-2" data-testid="impl-phase-templates">
        {groups.map((g, gi) => {
          const phaseCount = (g.phases || []).length;
          const totalWeeks = (g.phases || []).reduce((s, p) => s + Number(p.duration_weeks || 0), 0);
          const isOpen = !!expanded[gi];
          return (
            <div key={gi} data-testid={`impl-inputs-group-${gi}`} className="border border-[#025979]/20 rounded-sm bg-[#F8FAFC]">
              {/* Compact header row: reorder · editable name · summary · expand · add phase · remove */}
              <div className="flex items-center gap-2 px-3 py-2">
                <ArrowStack
                  testid={`impl-inputs-group-reorder-${gi}`}
                  canUp={gi > 0}
                  canDown={gi < groups.length - 1}
                  onUp={() => moveGroup(gi, -1)}
                  onDown={() => moveGroup(gi, +1)}
                />
                <Input
                  data-testid={`impl-inputs-group-name-${gi}`}
                  value={g.name}
                  onChange={(e) => updateGroup(gi, { name: e.target.value })}
                  className="rounded-sm text-xs font-semibold max-w-[260px]"
                />
                <span className="text-[10px] text-[#6B7280]" data-testid={`impl-inputs-group-summary-${gi}`}>
                  — {totalWeeks} {totalWeeks === 1 ? "week" : "weeks"} · {phaseCount} {phaseCount === 1 ? "phase" : "phases"}
                </span>
                <button
                  type="button"
                  data-testid={`impl-inputs-group-toggle-${gi}`}
                  onClick={() => setExpanded((e) => ({ ...e, [gi]: !e[gi] }))}
                  aria-label={isOpen ? "Collapse" : "Expand"}
                  aria-expanded={isOpen}
                  className="ml-auto text-[#025979] hover:bg-[#E6F1F5] rounded-sm p-0.5"
                >
                  {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5 rotate-90" />}
                </button>
                <Button
                  data-testid={`impl-inputs-add-phase-${gi}`}
                  onClick={() => { setExpanded((e) => ({ ...e, [gi]: true })); addPhase(gi); }}
                  variant="outline"
                  className="rounded-sm text-xs"
                >
                  <Plus className="w-3 h-3 mr-1" /> Phase
                </Button>
                <button
                  data-testid={`impl-inputs-group-remove-${gi}`}
                  onClick={() => removeGroup(gi)}
                  className="text-[#9CA3AF] hover:text-red-500"
                  aria-label="Remove phase group"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              {/* iter58: timing + scope row (always visible) */}
              <PhaseGroupScopeRow
                gi={gi}
                group={g}
                isRollout={isRollout(g)}
                isFoundation={isFoundation(g)}
                isPost={isPost(g)}
                timing={timingByIndex[gi]}
                allTypes={allTypes}
                assignedByType={assignedByType}
                typeTotal={typeTotal}
                scopeCap={scopeCap}
                typeLabel={typeLabel}
                onAdd={(k) => addAssignment(gi, k)}
                onRemove={(k) => removeAssignment(gi, k)}
                onChange={(k, v) => changeAssignmentCount(gi, k, v)}
              />
              {isOpen && (
                <div className="p-3 pt-0">
                  <div className="border border-[#E5E7EB] rounded-sm divide-y divide-[#E5E7EB] bg-white">
                    {(g.phases || []).map((p, pi) => (
                      <div key={p.id || pi} data-testid={`impl-inputs-phase-${gi}-${pi}`} className="px-3 py-2 flex items-center gap-2">
                        <span className="text-[10px] text-[#9CA3AF] w-5">{pi + 1}</span>
                        <ArrowStack
                          testid={`impl-inputs-phase-reorder-${gi}-${pi}`}
                          canUp={pi > 0}
                          canDown={pi < (g.phases || []).length - 1}
                          onUp={() => movePhase(gi, pi, -1)}
                          onDown={() => movePhase(gi, pi, +1)}
                        />
                        <Input
                          data-testid={`impl-inputs-phase-name-${gi}-${pi}`}
                          value={p.name}
                          onChange={(e) => updatePhase(gi, pi, { name: e.target.value })}
                          className="rounded-sm text-xs flex-1"
                        />
                        <Input
                          data-testid={`impl-inputs-phase-dur-${gi}-${pi}`}
                          type="number"
                          min={0}
                          step={0.5}
                          value={p.duration_weeks}
                          onChange={(e) => updatePhase(gi, pi, { duration_weeks: Number(e.target.value) })}
                          className="rounded-sm text-xs w-24"
                        />
                        <span className="text-[10px] text-[#9CA3AF] w-10">weeks</span>
                        <button
                          data-testid={`impl-inputs-phase-remove-${gi}-${pi}`}
                          onClick={() => removePhase(gi, pi)}
                          className="text-[#9CA3AF] hover:text-red-500"
                          aria-label="Remove phase"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                    {(g.phases || []).length === 0 && (
                      <div className="px-3 py-3 text-center text-[11px] text-[#9CA3AF]">No phases yet. Click <strong>Phase</strong> above to add one.</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {groups.length === 0 && (
          <div className="text-xs text-[#9CA3AF] italic">No implementation stages yet. Click "Add stage" to start.</div>
        )}
      </div>
    </SectionCard>
  );
}

// -----------------------------------------------------------------------------
// Resources editor (name, delivery type, rates)
// -----------------------------------------------------------------------------
// Phase-Group scope row (iter 58) — timing + warehouse assignment per group
// -----------------------------------------------------------------------------
function PhaseGroupScopeRow({
  gi, group, isRollout, isFoundation, isPost, timing,
  allTypes, assignedByType, typeTotal, scopeCap, typeLabel,
  onAdd, onRemove, onChange,
}) {
  const assignments = Array.isArray(group.scope_assignments) ? group.scope_assignments : [];
  const assignedKeys = new Set(assignments.map((a) => a.warehouse_type));
  // Foundation stays program-wide (up to total # sites); rollout stages are
  // capped at the group's Sites in Scope.
  const capFor = (k) => (isFoundation ? (typeTotal(k) || 0) : (scopeCap ? scopeCap(k) : typeTotal(k)));
  // iter 59: soft model — a type can appear in multiple stages, so we only
  // filter types already assigned IN THIS STAGE, not across the whole program.
  const available = allTypes.filter((t) => !assignedKeys.has(t.key));
  const [picker, setPicker] = useState("");
  const classTag = isFoundation ? "Foundation · common" : isPost ? "Hypercare · program-wide" : "Rollout";

  return (
    <div
      data-testid={`impl-inputs-group-scope-${gi}`}
      className="px-3 py-2 flex items-start gap-3 flex-wrap border-t border-dashed border-[#E5E7EB]"
    >
      {/* Timing + class */}
      <div className="text-[10px] text-[#6B7280] uppercase tracking-wider shrink-0 min-w-[130px]">
        <p data-testid={`impl-inputs-group-timing-${gi}`}>
          Week {timing?.start ?? 0}–{timing?.end ?? 0}
        </p>
        {isRollout && timing?.goLiveMonth != null && (
          <p className="mt-0.5 normal-case">
            Go-live <span className="text-[#025979] font-semibold" data-testid={`impl-inputs-group-golive-${gi}`}>Month {timing.goLiveMonth}</span>
          </p>
        )}
        <p className="mt-0.5 normal-case text-[#9CA3AF]" data-testid={`impl-inputs-group-class-${gi}`}>{classTag}</p>
      </div>
      {/* Applies To */}
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">
          Applies to
          {isFoundation && <span className="normal-case text-[10px] text-[#9CA3AF] ml-1">(optional — Foundation is program-wide by default)</span>}
        </p>
        <div className="flex flex-wrap gap-1.5 items-center">
          {assignments.length === 0 ? (
            <span className="text-[10px] text-[#9CA3AF] italic" data-testid={`impl-inputs-group-scope-empty-${gi}`}>
              No sites assigned.
            </span>
          ) : (
            assignments.map((a) => {
              const total = capFor(a.warehouse_type);
              const lc = Number(a.location_count) || 0;
              return (
                <span
                  key={a.warehouse_type}
                  data-testid={`impl-inputs-group-chip-${gi}-${a.warehouse_type}`}
                  className="text-[10px] px-1.5 py-0.5 rounded-sm border inline-flex items-center gap-1 bg-[#E0F2FE] border-[#025979]/20 text-[#025979]"
                >
                  <span>{typeLabel(a.warehouse_type)}</span>
                  <input
                    type="number"
                    min="0"
                    max={total}
                    step="1"
                    value={lc}
                    data-testid={`impl-inputs-group-chip-${gi}-${a.warehouse_type}-count`}
                    onChange={(e) => onChange(a.warehouse_type, e.target.value)}
                    className="w-10 text-[10px] px-1 py-px rounded-sm bg-white border border-[#025979]/20 text-[#025979] focus:outline-none focus:ring-1 focus:ring-[#025979]/40"
                    title={`Max ${total}`}
                  />
                  <span className="text-[9px]">of {total}</span>
                  <button
                    type="button"
                    data-testid={`impl-inputs-group-chip-${gi}-${a.warehouse_type}-remove`}
                    onClick={() => onRemove(a.warehouse_type)}
                    aria-label={`Remove ${typeLabel(a.warehouse_type)}`}
                    className="hover:text-[#C2410C]"
                  >
                    ×
                  </button>
                </span>
              );
            })
          )}
          {/* Always-rendered add-type control */}
          <span className="inline-flex items-center gap-1">
            <select
              data-testid={`impl-inputs-group-addtype-${gi}`}
              value={picker}
              onChange={(e) => setPicker(e.target.value)}
              disabled={available.length === 0}
              className="text-[10px] border border-[#E5E7EB] bg-white rounded-sm px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-[#025979]/40 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="">{available.length === 0 ? "All types assigned" : "Add type…"}</option>
              {available.map((t) => (
                <option key={t.key} value={t.key}>
                  {`${t.label} (${typeTotal(t.key)} ${typeTotal(t.key) === 1 ? "site" : "sites"})`}
                </option>
              ))}
            </select>
            <button
              type="button"
              data-testid={`impl-inputs-group-addtype-confirm-${gi}`}
              onClick={() => { if (picker) { onAdd(picker); setPicker(""); } }}
              disabled={!picker}
              className="text-[10px] px-1.5 py-0.5 rounded-sm border border-[#025979] text-[#025979] hover:bg-[#E6F1F5] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Plus className="w-3 h-3 inline" />
            </button>
          </span>
        </div>
      </div>
    </div>
  );
}


// -----------------------------------------------------------------------------
function ResourcesEditor({ resources, groups, onChange }) {
  const reindex = (arr) => arr.map((x, i) => ({ ...x, order: i }));

  const addResource = () => {
    const existing = new Set(resources.map((r) => r.name));
    let name = "New resource";
    let n = 2;
    while (existing.has(name)) { name = `New resource ${n}`; n += 1; }
    const next = [...resources, {
      name,
      onsite_rate: 100,
      offshore_rate: 60,
      delivery_type: "offshore",
      order: resources.length,
    }];
    // Seed every phase's effort matrix with 40 hrs for the new resource so
    // the grid isn't full of silent zeros.
    const nextGroups = (groups || []).map((g) => ({
      ...g,
      phases: (g.phases || []).map((p) => ({
        ...p,
        effort: { ...(p.effort || {}), [name]: DEFAULT_NEW_HOURS },
      })),
    }));
    onChange(next, nextGroups);
  };

  const removeResource = (idx) => {
    const name = resources[idx]?.name;
    const next = reindex(resources.filter((_, i) => i !== idx));
    const nextGroups = (groups || []).map((g) => ({
      ...g,
      phases: (g.phases || []).map((p) => {
        const effort = { ...(p.effort || {}) };
        if (name && name in effort) delete effort[name];
        return { ...p, effort };
      }),
    }));
    onChange(next, nextGroups);
  };

  const updateResource = (idx, patch) => {
    const oldName = resources[idx]?.name;
    const next = resources.map((r, i) => i === idx ? { ...r, ...patch } : r);
    let nextGroups = groups;
    if (patch.name !== undefined && patch.name !== oldName) {
      // Rename the key across every phase's effort dict so the grid stays in sync.
      nextGroups = (groups || []).map((g) => ({
        ...g,
        phases: (g.phases || []).map((p) => {
          const effort = { ...(p.effort || {}) };
          if (oldName && Object.prototype.hasOwnProperty.call(effort, oldName)) {
            effort[patch.name] = effort[oldName];
            delete effort[oldName];
          }
          return { ...p, effort };
        }),
      }));
    }
    onChange(next, nextGroups);
  };

  const moveResource = (idx, dir) => {
    const next = [...resources];
    const t = idx + dir;
    if (t < 0 || t >= next.length) return;
    [next[idx], next[t]] = [next[t], next[idx]];
    onChange(reindex(next));
  };

  return (
    <SectionCard
      title="Resources"
      description="Define the delivery team. Choose onsite or offshore per role — rates flip accordingly."
      action={
        <Button data-testid="impl-inputs-add-resource" onClick={addResource} variant="outline" className="rounded-sm text-xs">
          <Plus className="w-3 h-3 mr-1" /> Add resource
        </Button>
      }
    >
      <div className="border border-[#E5E7EB] rounded-sm divide-y divide-[#E5E7EB]">
        {resources.map((r, idx) => (
          <div
            key={idx}
            data-testid={`impl-inputs-res-${idx}`}
            className="px-3 py-2 flex flex-wrap items-center gap-2"
          >
            <span className="text-[10px] text-[#9CA3AF] w-5">{idx + 1}</span>
            <ArrowStack
              testid={`impl-inputs-res-reorder-${idx}`}
              canUp={idx > 0}
              canDown={idx < resources.length - 1}
              onUp={() => moveResource(idx, -1)}
              onDown={() => moveResource(idx, +1)}
            />
            <Input
              data-testid={`impl-inputs-res-name-${idx}`}
              value={r.name}
              onChange={(e) => updateResource(idx, { name: e.target.value })}
              className="rounded-sm text-xs flex-1 min-w-[180px]"
            />
            <Select
              value={r.delivery_type || "offshore"}
              onValueChange={(v) => updateResource(idx, { delivery_type: v })}
            >
              <SelectTrigger data-testid={`impl-inputs-res-delivery-${idx}`} className="rounded-sm text-xs h-8 w-28">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DELIVERY_TYPES.map((d) => (
                  <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <RateInput
              testid={`impl-inputs-res-onsite-${idx}`}
              label="Onsite"
              value={r.onsite_rate}
              onChange={(v) => updateResource(idx, { onsite_rate: v })}
            />
            <RateInput
              testid={`impl-inputs-res-offshore-${idx}`}
              label="Offshore"
              value={r.offshore_rate}
              onChange={(v) => updateResource(idx, { offshore_rate: v })}
            />
            <span className="text-[10px] text-[#9CA3AF]">
              Effective:{" "}
              <span className="text-[#025979] font-medium">
                ${r.delivery_type === "onsite" ? (r.onsite_rate || 0) : (r.offshore_rate || 0)}/hr
              </span>
            </span>
            <button
              data-testid={`impl-inputs-res-remove-${idx}`}
              onClick={() => removeResource(idx)}
              className="text-[#9CA3AF] hover:text-red-500"
              aria-label="Remove resource"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        {resources.length === 0 && (
          <div className="px-3 py-4 text-center text-[11px] text-[#9CA3AF]">No resources yet.</div>
        )}
      </div>
    </SectionCard>
  );
}

function RateInput({ label, value, onChange, testid }) {
  return (
    <label className="flex items-center gap-1 text-[10px] text-[#6B7280]">
      <span className="uppercase tracking-wider">{label}</span>
      <span className="text-[#9CA3AF]">$</span>
      <Input
        data-testid={testid}
        type="number"
        min={0}
        step={1}
        value={value ?? 0}
        onChange={(e) => onChange(Number(e.target.value))}
        className="rounded-sm text-xs w-20"
      />
      <span className="text-[#9CA3AF]">/hr</span>
    </label>
  );
}

// -----------------------------------------------------------------------------
// Effort grid (sticky headers + sticky left column)
// -----------------------------------------------------------------------------
function EffortGrid({ groups, resources, onUpdateCell }) {
  const flatPhases = [];
  (groups || []).forEach((g, gi) => {
    (g.phases || []).forEach((p, pi) => {
      flatPhases.push({ gi, pi, groupName: g.name, phase: p });
    });
  });

  const headerSpans = (groups || [])
    .map((g) => ({ name: g.name, colSpan: (g.phases || []).length }))
    .filter((h) => h.colSpan > 0);

  return (
    <SectionCard
      title="Weekly effort matrix"
      description="Hours per resource per week for each phase. Cells follow the admin template; edit freely."
    >
      {(!flatPhases.length || !resources.length) ? (
        <p className="text-xs text-[#9CA3AF] italic">Add at least one phase and one resource to edit the matrix.</p>
      ) : (
        <div className="overflow-x-auto max-h-[70vh] overflow-y-auto border border-[#E5E7EB] rounded-sm" data-testid="impl-inputs-grid">
          <table className="min-w-full text-xs border-collapse">
            <thead>
              <tr>
                <th className="sticky left-0 top-0 z-30 bg-white border-b border-r border-[#E5E7EB]" style={{ minWidth: 220 }} />
                {headerSpans.map((h, i) => (
                  <th
                    key={`gh-${i}`}
                    colSpan={h.colSpan}
                    data-testid={`impl-inputs-grid-group-${i}`}
                    className="sticky top-0 z-20 px-3 py-1.5 text-[10px] uppercase tracking-wider text-[#025979] font-semibold bg-[#E0F2FE] border-b-2 border-r border-[#025979]/20"
                  >
                    {h.name}
                  </th>
                ))}
              </tr>
              <tr>
                <th
                  className="sticky left-0 top-[28px] z-30 text-left px-3 py-2 text-[10px] uppercase tracking-wider text-[#9CA3AF] bg-white border-b border-r border-[#E5E7EB]"
                  style={{ minWidth: 220 }}
                >
                  Resource \ Phase
                </th>
                {flatPhases.map(({ phase }, i) => (
                  <th
                    key={`ph-${i}`}
                    className="sticky top-[28px] z-20 bg-white px-3 py-2 text-left text-[10px] uppercase tracking-wider text-[#9CA3AF] border-b border-[#E5E7EB] min-w-[90px]"
                  >
                    {phase.name}
                    <div className="text-[9px] text-[#9CA3AF] font-normal normal-case tracking-normal">{phase.duration_weeks}w</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {resources.map((r, ri) => {
                const rate = r.delivery_type === "onsite" ? r.onsite_rate : r.offshore_rate;
                return (
                  <tr key={`r-${ri}`} data-testid={`impl-inputs-grid-row-${ri}`}>
                    <th
                      className="sticky left-0 z-10 text-left px-3 py-2 bg-white font-medium text-[#111827] border-b border-r border-[#E5E7EB]"
                      style={{ minWidth: 220 }}
                    >
                      <div className="flex items-center gap-1.5">
                        <span>{r.name}</span>
                        <span className={`text-[9px] uppercase tracking-wider px-1 py-0.5 rounded-sm ${
                          r.delivery_type === "onsite"
                            ? "bg-[#FEF3C7] text-[#92400E]"
                            : "bg-[#DBEAFE] text-[#1E40AF]"
                        }`}>{r.delivery_type || "offshore"}</span>
                      </div>
                      <div className="text-[10px] text-[#6B7280] font-normal">${rate || 0}/hr</div>
                    </th>
                    {flatPhases.map(({ gi, pi, phase }, ci) => {
                      const v = phase.effort?.[r.name] ?? 0;
                      return (
                        <td key={`c-${ri}-${ci}`} className="px-1 py-1 border-b border-[#E5E7EB]">
                          <input
                            data-testid={`impl-inputs-cell-${ri}-${gi}-${pi}`}
                            type="number"
                            min={0}
                            step={1}
                            value={v}
                            onChange={(e) => onUpdateCell(gi, pi, r.name, e.target.value)}
                            className="w-16 text-right rounded-sm border border-[#E5E7EB] px-2 py-1 text-xs focus:border-[#025979] focus:outline-none"
                          />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}

// -----------------------------------------------------------------------------
// Shared UI bits
// -----------------------------------------------------------------------------
function SectionCard({ title, description, action, children }) {
  return (
    <section className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm">
      <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-[#111827] flex items-center gap-1.5" style={{ fontFamily: "Chivo" }}>
            <Layers className="w-3.5 h-3.5 text-[#025979]" />{title}
          </h3>
          {description && <p className="text-[11px] text-[#6B7280] mt-0.5">{description}</p>}
        </div>
        {action}
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

function ArrowStack({ canUp, canDown, onUp, onDown, testid }) {
  const base = "w-5 h-5 flex items-center justify-center rounded-sm border text-[#6B7280] transition-colors";
  const enabled = "border-[#E5E7EB] hover:bg-[#E0F2FE] hover:text-[#025979] hover:border-[#025979]/30";
  const disabled = "border-[#F3F4F6] text-[#E5E7EB] cursor-not-allowed";
  return (
    <div className="inline-flex flex-col gap-0.5" data-testid={testid}>
      <button type="button" data-testid={`${testid}-up`} onClick={canUp ? onUp : undefined} disabled={!canUp}
              aria-label="Move up" className={`${base} ${canUp ? enabled : disabled}`}>
        <ChevronUp className="w-3 h-3" />
      </button>
      <button type="button" data-testid={`${testid}-down`} onClick={canDown ? onDown : undefined} disabled={!canDown}
              aria-label="Move down" className={`${base} ${canDown ? enabled : disabled}`}>
        <ChevronDown className="w-3 h-3" />
      </button>
    </div>
  );
}


// -----------------------------------------------------------------------------
// Phase 2: Implementation Strategy section
// -----------------------------------------------------------------------------
function ImplementationStrategySection({ inputs, programme, isGrouped = false, onUpdate, onRegeneratePhaseGroups, saving }) {
  const mode = inputs.rollout_mode || "pilot_phased";
  // Feb 2026 — Lean Deployment added as a third strategy for SAP LGM /
  // embedded warehouse / lightweight programs. Display order is fixed:
  // Lean Deployment → Pilot + Rollout → Big Bang.
  const modes = [
    { v: "lean_deployment", l: "Lean Deployment" },
    { v: "pilot_phased",    l: "Pilot + Rollout" },
    { v: "big_bang",        l: "Big Bang" },
  ];
  const needsModeMigration = mode === "parallel";

  // --- Program Scope: single vs multi-site handling -----------------------
  // Replaces the old "Primary warehouse" line with a clearer decomposition:
  //   Solution · Deployment · Warehouse Type(s) · Total Locations ·
  //   Implementation Scope (Single-site rollout | Multi-site transformation)
  const totalLocations = Number(inputs.total_warehouses || 0);
  const rolloutRows = Array.isArray(inputs.rollout) ? inputs.rollout : [];
  // Always include the primary warehouse type first, then any rollout types.
  const scopedTypes = [];
  const seenTypes = new Set();
  if (inputs.primary_warehouse_type_label) {
    scopedTypes.push(inputs.primary_warehouse_type_label);
    if (inputs.primary_warehouse_type) seenTypes.add(inputs.primary_warehouse_type);
  }
  for (const r of rolloutRows) {
    if (!r.warehouse_type_label) continue;
    if (Number(r.warehouse_count || 0) <= 0) continue;
    if (r.warehouse_type && seenTypes.has(r.warehouse_type)) continue;
    scopedTypes.push(r.warehouse_type_label);
    if (r.warehouse_type) seenTypes.add(r.warehouse_type);
  }
  const typeList = scopedTypes;
  const isSingleSite = totalLocations <= 1;
  const implementationScope = isSingleSite ? "Single-site rollout" : "Multi-site transformation";
  const shortTypeName = (label) => {
    if (!label) return label;
    const lut = {
      "Central Distribution Center": "CDC",
      "Regional Distribution Center": "RDC",
      "Manufacturing Plant Warehouse": "Manufacturing",
      "E-commerce Fulfillment Center": "E-comm",
      "Spare Parts Warehouse": "Spare Parts",
    };
    return lut[label] || label;
  };

  const deployment =
    inputs.deployment_type
    || inputs.overall_deployment_type
    || inputs.deployment_label
    || "";

  // Programme-level recommendation context (effective / override-aware) — kept
  // in lock-step with the Recommendations header via deriveProgrammeContext.
  // Falls back to the local implementation-inputs values when unavailable.
  const solutionStrategy = (programme?.solutionDisplay && programme.solutionDisplay !== "\u2014")
    ? programme.solutionDisplay
    : (inputs.primary_solution_label || null);
  const deploymentArchitecture = (programme?.deployment && programme.deployment !== "\u2014")
    ? programme.deployment
    : (deployment || null);
  const hostingModel = programme?.hostingModel || "Not Specified";

  const scopeRow = (label, value, testid) => (
    <div className="flex flex-wrap items-baseline gap-x-2" data-testid={testid}>
      <span className="text-[11px] uppercase tracking-wider text-[#6B7280] shrink-0 w-40">
        {label}
      </span>
      <span className="text-sm text-[#0F172A] font-medium">{value}</span>
    </div>
  );

  return (
    <section
      data-testid="impl-strategy-section"
      className="rounded-sm border border-[#E5E7EB] p-4 bg-white"
    >
      <h3 className="text-xs font-semibold text-[#025979] uppercase tracking-wider mb-1">
        Implementation Strategy
      </h3>
      <p
        data-testid="impl-strategy-helper"
        className="text-[11px] text-[#6B7280] mb-3 leading-snug"
      >
        Implementation approach should align to the selected transformation pattern, deployment architecture, and hosting model.
      </p>

      <div className={isSingleSite ? "grid grid-cols-1 gap-5" : "grid grid-cols-1 md:grid-cols-2 gap-5"}>
        {/* Rollout Approach — hidden for single-site (only one fixed wave) */}
        {!isSingleSite && (
        <div>
          <p className="block text-[10px] text-[#6B7280] uppercase tracking-wider mb-2">
            Rollout Approach
          </p>
          <div
            role="radiogroup"
            aria-label="Rollout approach"
            data-testid="impl-rollout-mode"
            className="inline-flex rounded-sm border border-[#D1D5DB] bg-[#F9FAFB] p-0.5 overflow-hidden"
          >
            {modes.map((m) => {
              const active = mode === m.v;
              return (
                <button
                  key={m.v}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  data-testid={`impl-rollout-mode-${m.v}`}
                  onClick={() => {
                    if (m.v === mode) return;
                    // iter 58: changing approach regenerates Phase Groups
                    // with the right smart-default structure per mode.
                    if (onRegeneratePhaseGroups) onRegeneratePhaseGroups(m.v);
                    else onUpdate({ rollout_mode: m.v });
                  }}
                  disabled={saving}
                  className={[
                    "px-3 py-1.5 text-xs font-semibold rounded-[2px] transition-colors",
                    active
                      ? "bg-[#025979] text-white shadow-sm"
                      : "bg-transparent text-[#475569] hover:text-[#0F172A] hover:bg-white",
                  ].join(" ")}
                >
                  {m.l}
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-[#6B7280] mt-2">
            Drives the default Phase Groups structure (Lean Deployment, Foundation + Pilot + Wave 1, or Foundation + Big Bang).
            Picking a new approach will regenerate Phase Groups with default warehouse assignments.
          </p>
        </div>
        )}

        {/* iter 58: Parallel mode migration banner — legacy assessments only */}
        {needsModeMigration && (
          <div
            data-testid="impl-rollout-mode-migration-banner"
            className="md:col-span-2 rounded-sm border border-[#F59E0B]/40 bg-[#FEF3C7] px-3 py-2.5"
          >
            <p className="text-[11px] font-semibold text-[#92400E]">
              This assessment is on the retired <strong>Parallel</strong> rollout approach.
            </p>
            <p className="text-[11px] text-[#92400E] mt-0.5">
              Pick either <strong>Pilot + Rollout</strong> or <strong>Big Bang</strong>. Phase Groups will be regenerated with smart defaults.
            </p>
            <div className="flex gap-2 mt-2">
              <Button
                data-testid="impl-rollout-migration-pilot_phased"
                variant="outline"
                disabled={saving}
                onClick={() => onRegeneratePhaseGroups && onRegeneratePhaseGroups("pilot_phased")}
                className="rounded-sm text-xs border-[#92400E] text-[#92400E] hover:bg-[#FDE68A]"
              >
                Switch to Pilot + Rollout
              </Button>
              <Button
                data-testid="impl-rollout-migration-big_bang"
                variant="outline"
                disabled={saving}
                onClick={() => onRegeneratePhaseGroups && onRegeneratePhaseGroups("big_bang")}
                className="rounded-sm text-xs border-[#92400E] text-[#92400E] hover:bg-[#FDE68A]"
              >
                Switch to Big Bang
              </Button>
            </div>
          </div>
        )}

        {/* Program Scope — clearer single vs multi-site breakdown */}
        <div>
          <p className="block text-[10px] text-[#6B7280] uppercase tracking-wider mb-2">
            Program Scope
          </p>
          <div
            data-testid="impl-program-scope"
            className="space-y-1.5 rounded-sm border border-[#E5E7EB] bg-[#F8FAFC] px-3 py-2.5"
          >
            {solutionStrategy
              ? scopeRow("Solution Strategy", solutionStrategy, "impl-scope-solution")
              : null}
            {deploymentArchitecture
              ? scopeRow("Deployment Architecture", deploymentArchitecture, "impl-scope-deployment")
              : null}
            {scopeRow("Hosting Recommendation", hostingModel, "impl-scope-hosting")}
            {typeList.length > 0
              ? scopeRow(
                  isGrouped
                    ? (typeList.length === 1 ? "Warehouse Group" : "Warehouse Groups")
                    : (typeList.length === 1 ? "Warehouse Type" : "Warehouse Types"),
                  typeList.length === 1
                    ? typeList[0]
                    : typeList.map(shortTypeName).join(" + "),
                  "impl-scope-types",
                )
              : null}
            {totalLocations > 0
              ? scopeRow(
                  "Total Sites",
                  String(totalLocations),
                  "impl-scope-total-locations",
                )
              : null}
            {scopeRow(
              "Implementation Scope",
              implementationScope,
              "impl-scope-implementation-scope",
            )}
            {solutionStrategy || typeList.length > 0
              ? null
              : (
                <p className="text-[11px] text-[#9CA3AF] italic">
                  Scope will appear once scoring completes.
                </p>
              )}
          </div>
        </div>
      </div>

      <div className="mt-4">
        <label
          className="block text-[10px] text-[#6B7280] uppercase tracking-wider mb-1"
          htmlFor="impl-program-notes"
        >
          Notes / Assumptions
        </label>
        <textarea
          id="impl-program-notes"
          data-testid="impl-program-notes"
          rows={2}
          value={inputs.program_notes || ""}
          onChange={(e) => onUpdate({ program_notes: e.target.value })}
          className="w-full rounded-sm border border-[#D1D5DB] bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#025979]/40 focus:border-[#025979]/50"
          placeholder="e.g. Pilot in Manufacturing first, then CDC, then RDC network."
        />
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Phase 2: Cost Model section
// -----------------------------------------------------------------------------
// -----------------------------------------------------------------------------
// Rollout Waves — flexible, consultant-driven editor
// -----------------------------------------------------------------------------
const WH_SHORT = {
  cdc: "CDC", rdc: "RDC", manufacturing: "Manufacturing",
  ecommerce: "E-comm", spare_parts: "Spare Parts", satellite: "Satellite",
};
const WH_FULL = {
  cdc: "Central Distribution Center",
  rdc: "Regional Distribution Center",
  manufacturing: "Manufacturing Plant Warehouse",
  ecommerce: "E-commerce Fulfillment Center",
  spare_parts: "Spare Parts Warehouse",
  satellite: "Satellite Warehouse",
};
const WEEKS_PER_MONTH = 4.345;
const PARALLEL_STAGGER_WEEKS = 4;

function shortLbl(k) { return WH_SHORT[k] || k; }
function fullLbl(k) { return WH_FULL[k] || k; }

function WavesSection({ inputs, onUpdate, onRegenerate, saving, onMessage }) {
  const waves = Array.isArray(inputs.waves) ? inputs.waves : [];
  const phaseGroups = Array.isArray(inputs.phase_groups) ? inputs.phase_groups : [];
  const mode = inputs.rollout_mode || "pilot_phased";
  const autoGenerated = !!inputs.waves_auto_generated;
  const currency = inputs.cost_breakdown?.currency || "USD";
  const totalLocations = Number(inputs.total_warehouses || 0);
  const isSingleSite = totalLocations <= 1;
  const monthsFromWeeks = (w) => (Number(w) > 0 ? (Number(w) / WEEKS_PER_MONTH).toFixed(1) : "0");
  const rolloutLabel = {
    lean_deployment: "Lean Deployment",
    pilot_phased: "Pilot → Phased",
    parallel: "Parallel",
    big_bang: "Big Bang",
  }[mode] || mode;
  const fmt = (n) => `${currency} ${Math.round(Number(n || 0)).toLocaleString()}`;

  // --- Phase group classification (pre / rollout / post) ---------------
  const FOUNDATION_RE = /foundation|discover|blueprint|prepare/i;
  const POST_RE = /hypercare|stabili[sz]e|stabili[sz]ation/i;
  const pgDuration = (pg) =>
    (pg?.phases || []).reduce((s, p) => s + Number(p.duration_weeks || 0), 0);
  const pgMeta = phaseGroups.map((g, i) => {
    const name = g?.name || `Group ${i + 1}`;
    const cls = FOUNDATION_RE.test(name) ? "pre" : POST_RE.test(name) ? "post" : "rollout";
    return { index: i, name, duration_weeks: pgDuration(g), cls };
  });
  const preWeeks = pgMeta.filter((m) => m.cls === "pre").reduce((s, m) => s + m.duration_weeks, 0);
  const postWeeks = pgMeta.filter((m) => m.cls === "post").reduce((s, m) => s + m.duration_weeks, 0);
  const totalProgramWeeks = pgMeta.reduce((s, m) => s + m.duration_weeks, 0);

  const getPgMeta = (idx) => (idx != null && idx >= 0 && idx < pgMeta.length ? pgMeta[idx] : null);
  const durationOf = (w) => {
    const meta = getPgMeta(w.phase_group_index);
    if (meta) return meta.duration_weeks;
    // Fallback for legacy waves without phase_group_index
    return Number(w.duration_weeks || 0);
  };

  // Available warehouse types (primary + rollout) ------------------------
  const allTypes = useMemo(() => {
    const m = new Map();
    if (inputs.primary_warehouse_type) {
      m.set(inputs.primary_warehouse_type, {
        key: inputs.primary_warehouse_type,
        label: inputs.primary_warehouse_type_label || fullLbl(inputs.primary_warehouse_type),
        count: Number(inputs.primary_warehouse_count || 0) || 1,
      });
    }
    for (const r of inputs.rollout || []) {
      if (!r.warehouse_type || m.has(r.warehouse_type)) continue;
      m.set(r.warehouse_type, {
        key: r.warehouse_type,
        label: r.warehouse_type_label || fullLbl(r.warehouse_type),
        count: Number(r.warehouse_count || 0) || 1,
      });
    }
    return Array.from(m.values());
  }, [inputs.primary_warehouse_type, inputs.primary_warehouse_type_label, inputs.primary_warehouse_count, inputs.rollout]);

  const typeCount = (k) => (allTypes.find((t) => t.key === k) || {}).count || 0;
  const typeLabel = (k) => (allTypes.find((t) => t.key === k) || {}).label || fullLbl(k);

  // Network-wide per-type counts + assignment aggregates ----------------
  const networkTotalByType = useMemo(() => {
    const m = {};
    allTypes.forEach((t) => { m[t.key] = t.count; });
    return m;
  }, [allTypes]);

  // Aggregate currently-assigned per type across ALL waves
  const assignedByType = useMemo(() => {
    const m = {};
    for (const w of waves) {
      const ass = Array.isArray(w.scope_assignments) ? w.scope_assignments : null;
      if (ass && ass.length) {
        for (const a of ass) {
          const k = a.warehouse_type;
          if (!k) continue;
          m[k] = (m[k] || 0) + (Number(a.location_count) || 0);
        }
      } else {
        // Legacy: distribute location_count across scope_type_keys equally
        const keys = w.scope_type_keys || [];
        const lc = Number(w.location_count || 0);
        if (keys.length && lc > 0) {
          const per = Math.floor(lc / keys.length);
          const rem = lc - per * keys.length;
          keys.forEach((k, i) => {
            m[k] = (m[k] || 0) + per + (i === keys.length - 1 ? rem : 0);
          });
        }
      }
    }
    return m;
  }, [waves]);

  const remainingByType = useMemo(() => {
    const m = {};
    allTypes.forEach((t) => {
      m[t.key] = Math.max(0, t.count - (assignedByType[t.key] || 0));
    });
    return m;
  }, [allTypes, assignedByType]);

  const totalNetworkLocations = allTypes.reduce((s, t) => s + t.count, 0);
  const totalAssigned = Object.values(assignedByType).reduce((s, v) => s + v, 0);
  const hasOverAssignment = allTypes.some((t) => (assignedByType[t.key] || 0) > t.count);

  const newWaveId = () => `w-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;

  // Recompute derived fields (timing from phase_groups, effort_share, cost) ----
  const recompute = (arr) => {
    const out = arr.map((w) => {
      const next = { ...w, duration_weeks: durationOf(w) };
      // Rebuild derived scope fields from scope_assignments (authoritative)
      const ass = Array.isArray(next.scope_assignments) ? next.scope_assignments : null;
      if (ass) {
        next.scope_type_keys = ass.map((a) => a.warehouse_type);
        next.scope_labels = next.scope_type_keys.map(typeLabel);
        next.scope_types = next.scope_type_keys.map(shortLbl);
        next.location_count = ass.reduce((s, a) => s + Number(a.location_count || 0), 0);
      }
      return next;
    });
    // Timing: sequential from preWeeks (foundation offset). Parallel mode
    // is treated as sequential with a note (overlap not modeled yet).
    let cursor = preWeeks;
    out.forEach((w) => {
      const dur = Number(w.duration_weeks || 0);
      const end = cursor + dur;
      w.start_week = Math.round(cursor);
      w.end_week = Math.round(end);
      w.go_live_month = end > 0 ? Math.max(1, Math.ceil(end / WEEKS_PER_MONTH)) : 1;
      cursor = end;
    });
    // Effort share — complexity-weighted by scope types × locations
    const weight = (w) => {
      const keys = w.scope_type_keys || [];
      const locs = Math.max(1, Number(w.location_count || 0));
      if (!keys.length) return locs;
      let wt = 0;
      for (const k of keys) {
        const bias = (k === "manufacturing" || k === "cdc") ? 1.5 : 1.0;
        wt += bias * (locs / keys.length);
      }
      return wt || 1;
    };
    const total = out.reduce((s, w) => s + weight(w), 0) || 1;
    out.forEach((w) => {
      w.effort_share = Math.round((weight(w) / total) * 10000) / 10000;
    });
    const totOneTime = Number(inputs.cost_breakdown?.one_time?.total_one_time_investment || 0);
    const totRecurring = Number(inputs.cost_breakdown?.recurring?.total_annual_recurring || 0);
    out.forEach((w) => {
      w.allocated_one_time = Math.round((totOneTime * w.effort_share) * 100) / 100;
      w.allocated_recurring = Math.round((totRecurring * w.effort_share) * 100) / 100;
    });
    return out;
  };

  const commit = (nextWaves) => {
    const normalized = recompute(nextWaves);
    onUpdate({ waves: normalized, waves_auto_generated: false });
  };

  // Actions --------------------------------------------------------------
  const moveUp = (i) => {
    if (i <= 0) return;
    const next = [...waves];
    [next[i - 1], next[i]] = [next[i], next[i - 1]];
    commit(next);
  };
  const moveDown = (i) => {
    if (i >= waves.length - 1) return;
    const next = [...waves];
    [next[i], next[i + 1]] = [next[i + 1], next[i]];
    commit(next);
  };
  const splitWave = (i) => {
    // Create an empty wave after the current one. Keep current wave intact
    // (no scope moves) — consultant explicitly assigns types to the new one.
    const w = waves[i];
    const right = {
      id: newWaveId(),
      name: `${w.name || "Wave"} (Part 2)`,
      description: "",
      scope_assignments: [],
      phase_group_index: w.phase_group_index ?? null,
    };
    commit([...waves.slice(0, i + 1), right, ...waves.slice(i + 1)]);
    if (onMessage) onMessage("New wave created. Move or assign sites to this wave.");
  };
  const mergeWithNext = (i) => {
    if (i >= waves.length - 1) return;
    const a = waves[i];
    const b = waves[i + 1];
    // Merge scope_assignments by warehouse_type (sum counts)
    const aAssign = getAssignments(a);
    const bAssign = getAssignments(b);
    const byType = new Map();
    for (const x of [...aAssign, ...bAssign]) {
      const prev = byType.get(x.warehouse_type) || 0;
      byType.set(x.warehouse_type, prev + Number(x.location_count || 0));
    }
    const merged = {
      ...a,
      // Retain current wave's phase_group_index by design (MVP rule).
      phase_group_index: a.phase_group_index ?? null,
      name: `${a.name || "Wave"} + ${b.name || "Wave"}`,
      scope_assignments: Array.from(byType, ([warehouse_type, location_count]) => ({ warehouse_type, location_count })),
      description: a.description || b.description || "",
    };
    commit([...waves.slice(0, i), merged, ...waves.slice(i + 2)]);
    if (onMessage) onMessage("Phase duration retained from current wave. Review duration if needed.");
  };
  const removeWave = (i) => {
    if (waves.length <= 1) return;
    commit(waves.filter((_, idx) => idx !== i));
  };
  const addWave = () => {
    // Default link: next rollout-class phase group not already linked
    const usedPg = new Set(waves.map((w) => w.phase_group_index).filter((x) => x != null));
    const defaultPg = pgMeta.find((m) => m.cls === "rollout" && !usedPg.has(m.index));
    const w = {
      id: newWaveId(),
      name: `Wave ${waves.length + 1}`,
      scope_assignments: [],
      description: "",
      phase_group_index: defaultPg ? defaultPg.index : null,
    };
    commit([...waves, w]);
  };
  const renameWave = (i, name) => {
    const next = [...waves];
    next[i] = { ...next[i], name };
    onUpdate({ waves: next, waves_auto_generated: false });
  };
  const changePhaseGroup = (i, rawIdx) => {
    const idx = rawIdx === "" || rawIdx === null || rawIdx === undefined
      ? null
      : Number(rawIdx);
    const next = [...waves];
    next[i] = { ...next[i], phase_group_index: Number.isFinite(idx) ? idx : null };
    commit(next);
  };
  const getAssignments = (w) => {
    if (Array.isArray(w.scope_assignments) && w.scope_assignments.length) {
      return w.scope_assignments;
    }
    // Legacy migration: infer from scope_type_keys + location_count
    const keys = w.scope_type_keys || [];
    const lc = Number(w.location_count || 0);
    if (!keys.length || lc <= 0) return [];
    const per = Math.max(1, Math.floor(lc / keys.length));
    const rem = lc - per * keys.length;
    return keys.map((k, i) => ({
      warehouse_type: k,
      location_count: per + (i === keys.length - 1 ? rem : 0),
    }));
  };

  const addAssignment = (i, key, count) => {
    const w = waves[i];
    const existing = getAssignments(w);
    if (!key || existing.find((a) => a.warehouse_type === key)) return;
    const cap = Math.max(0, (networkTotalByType[key] || 0) - (assignedByType[key] || 0));
    const take = Math.max(1, Math.min(Number(count) || 1, cap || 1));
    if (cap <= 0) {
      if (onMessage) onMessage(`No ${typeLabel(key)} sites remain to assign.`);
      return;
    }
    const next = [...waves];
    next[i] = { ...w, scope_assignments: [...existing, { warehouse_type: key, location_count: take }] };
    commit(next);
  };
  const removeAssignment = (i, key) => {
    const w = waves[i];
    const next = [...waves];
    next[i] = { ...w, scope_assignments: getAssignments(w).filter((a) => a.warehouse_type !== key) };
    commit(next);
  };
  const changeAssignmentCount = (i, key, rawCount) => {
    const w = waves[i];
    const existing = getAssignments(w);
    const current = existing.find((a) => a.warehouse_type === key);
    const oldCount = current ? Number(current.location_count) : 0;
    const total = networkTotalByType[key] || 0;
    const assignedElsewhere = (assignedByType[key] || 0) - oldCount;
    const cap = Math.max(0, total - assignedElsewhere);
    const want = Math.max(0, Number(rawCount) || 0);
    const finalCount = Math.min(want, cap);
    if (want > cap && onMessage) {
      onMessage(`Only ${cap} ${typeLabel(key)} ${cap === 1 ? "site" : "sites"} remain unassigned. Clamped to ${cap}.`);
    }
    const nextAssigns = existing.map((a) =>
      a.warehouse_type === key ? { ...a, location_count: finalCount } : a
    ).filter((a) => Number(a.location_count) > 0);
    const next = [...waves];
    next[i] = { ...w, scope_assignments: nextAssigns };
    commit(next);
  };

  // Single-site: force-collapse to one read-only wave -------------------
  const singleSiteWave = (() => {
    if (!isSingleSite) return null;
    const existing = waves[0] || {};
    const primaryKey = inputs.primary_warehouse_type || (allTypes[0] && allTypes[0].key) || null;
    const dur = durationOf({ ...existing, phase_group_index: existing.phase_group_index });
    return {
      id: existing.id || "w1",
      name: "Full implementation",
      duration_weeks: dur,
      scope_type_keys: primaryKey ? [primaryKey] : [],
      scope_labels: primaryKey ? [typeLabel(primaryKey)] : [],
      scope_types: primaryKey ? [shortLbl(primaryKey)] : [],
      location_count: 1,
      description: primaryKey
        ? `Single-site implementation for the ${typeLabel(primaryKey)}. End-to-end blueprint, build, test, and go-live in one wave.`
        : "Single-site implementation — end-to-end blueprint, build, test, and go-live in one wave.",
      start_week: preWeeks,
      end_week: preWeeks + dur,
      go_live_month: dur > 0 ? Math.max(1, Math.ceil((preWeeks + dur) / WEEKS_PER_MONTH)) : 1,
      effort_share: 1,
      allocated_one_time: Number(inputs.cost_breakdown?.one_time?.total_one_time_investment || 0),
      allocated_recurring: Number(inputs.cost_breakdown?.recurring?.total_annual_recurring || 0),
      phase_group_index: existing.phase_group_index ?? null,
    };
  })();

  const renderList = isSingleSite && singleSiteWave ? [singleSiteWave] : waves;
  const totalAlloc = renderList.reduce((n, w) => n + Number(w.allocated_one_time || 0), 0);

  return (
    <section
      data-testid="impl-waves-section"
      className="rounded-sm border border-[#E5E7EB] bg-white"
    >
      <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-semibold text-[#111827] flex items-center gap-1.5" style={{ fontFamily: "Chivo" }}>
            <Calendar className="w-3.5 h-3.5 text-[#025979]" />
            Rollout Waves
            {!isSingleSite && <span className="text-[10px] font-normal text-[#9CA3AF]">· {rolloutLabel}</span>}
          </h3>
          <p className="text-[11px] text-[#6B7280] mt-0.5" data-testid="impl-waves-meta">
            {isSingleSite
              ? "Single-site program — one fixed wave covers the full implementation."
              : (autoGenerated
                  ? "Synced with rollout approach. Reorder, split, merge, or reassign warehouse types below to override."
                  : "Manual overrides in effect — click Regenerate to reset to the intelligent default for the current rollout approach.")}
          </p>
          <p className="text-[11px] text-[#6B7280] mt-1" data-testid="impl-waves-duration-help">
            Wave duration is derived from the selected Phase Group (edit phase weeks in <em>Phase Groups</em> below).
            {preWeeks > 0 && ` Foundation offset: ${preWeeks} weeks before Wave 1.`}
            {postWeeks > 0 && ` Hypercare / stabilisation: +${postWeeks} weeks after last wave.`}
          </p>
        </div>
        {!isSingleSite && (
          <div className="flex items-center gap-2">
            <Button
              data-testid="impl-waves-add"
              onClick={addWave}
              disabled={saving}
              variant="outline"
              className="rounded-sm text-xs border-[#E5E7EB] text-[#374151] hover:bg-[#F3F4F6]"
            >
              <Plus className="w-3 h-3 mr-1.5" /> Add wave
            </Button>
            <Button
              data-testid="impl-waves-regenerate"
              onClick={onRegenerate}
              disabled={saving}
              variant="outline"
              className="rounded-sm text-xs border-[#025979] text-[#025979] hover:bg-[#E6F1F5]"
            >
              <RefreshCw className="w-3 h-3 mr-1.5" /> Regenerate from rollout mode
            </Button>
          </div>
        )}
      </div>

      {mode === "parallel" && !isSingleSite && (
        <div
          data-testid="impl-waves-parallel-note"
          className="mx-4 mt-3 px-3 py-2 bg-[#FEF3C7] border border-[#F59E0B]/30 rounded-sm text-[11px] text-[#92400E]"
        >
          <Info className="w-3 h-3 inline mr-1" />
          Parallel execution selected. Overlap is not modeled in this MVP; timeline is sequential unless manually adjusted.
        </div>
      )}

      {/* Assignment Validation Summary */}
      {!isSingleSite && allTypes.length > 0 && (
        <div
          data-testid="impl-waves-validation"
          className={`mx-4 mt-3 rounded-sm border px-3 py-2.5 ${
            hasOverAssignment
              ? "border-red-400 bg-red-50"
              : totalAssigned === totalNetworkLocations && totalNetworkLocations > 0
                ? "border-green-400 bg-green-50"
                : "border-[#F59E0B]/40 bg-[#FEF3C7]"
          }`}
        >
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <div>
              <p className="text-[10px] uppercase tracking-wider font-semibold text-[#374151]">Assignment Validation Summary</p>
              <p className="text-xs mt-0.5" data-testid="impl-waves-validation-totals">
                Total network: <strong>{totalNetworkLocations}</strong> {totalNetworkLocations === 1 ? "site" : "sites"} ·
                Assigned: <strong data-testid="impl-waves-validation-assigned">{totalAssigned}</strong> / {totalNetworkLocations} ·
                Remaining: <strong data-testid="impl-waves-validation-remaining">{Math.max(0, totalNetworkLocations - totalAssigned)}</strong>
              </p>
            </div>
            <div className="text-[11px] font-semibold" data-testid="impl-waves-validation-status">
              {hasOverAssignment
                ? <span className="text-red-700">⚠ Some sites are assigned more than once.</span>
                : totalAssigned === totalNetworkLocations && totalNetworkLocations > 0
                  ? <span className="text-green-700">✓ Full coverage</span>
                  : <span className="text-[#92400E]">⚠ Not all sites are assigned to rollout waves.</span>}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
            {allTypes.map((t) => {
              const assigned = assignedByType[t.key] || 0;
              const total = t.count;
              const over = assigned > total;
              const full = assigned === total;
              return (
                <div
                  key={t.key}
                  data-testid={`impl-waves-validation-${t.key}`}
                  className={`text-[11px] px-2 py-1 rounded-sm border ${
                    over ? "bg-red-100 border-red-300 text-red-700"
                         : full ? "bg-green-100 border-green-300 text-green-800"
                                : "bg-white border-[#E5E7EB] text-[#374151]"
                  }`}
                >
                  <span className="font-semibold">{t.label}</span>: <strong>{assigned}</strong> / {total}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="p-4">
        {renderList.length === 0 ? (
          <p className="text-xs text-[#9CA3AF] italic" data-testid="impl-waves-empty">
            No waves yet. Click <strong>Add wave</strong> to create one, or save the tab to seed waves from the current rollout approach.
          </p>
        ) : (
          <div className="space-y-3">
            {renderList.map((w, idx) => (
              <WaveCard
                key={w.id || idx}
                idx={idx}
                wave={w}
                readOnly={isSingleSite}
                total={renderList.length}
                allTypes={allTypes}
                remainingByType={remainingByType}
                networkTotalByType={networkTotalByType}
                pgMeta={pgMeta}
                monthsFromWeeks={monthsFromWeeks}
                fmt={fmt}
                assignmentsGetter={getAssignments}
                onMoveUp={() => moveUp(idx)}
                onMoveDown={() => moveDown(idx)}
                onSplit={() => splitWave(idx)}
                onMerge={() => mergeWithNext(idx)}
                onRemove={() => removeWave(idx)}
                onRename={(v) => renameWave(idx, v)}
                onPhaseGroup={(v) => changePhaseGroup(idx, v)}
                onAddAssignment={(k, c) => addAssignment(idx, k, c)}
                onRemoveAssignment={(k) => removeAssignment(idx, k)}
                onChangeCount={(k, c) => changeAssignmentCount(idx, k, c)}
              />
            ))}
            <div
              data-testid="impl-waves-total"
              className="flex items-baseline justify-between rounded-sm bg-[#E0F2FE] px-3 py-2 border border-[#025979]/20"
            >
              <span className="text-[10px] uppercase tracking-wider text-[#025979] font-semibold">
                Total Program Duration · {totalProgramWeeks} weeks ({monthsFromWeeks(totalProgramWeeks)} mo) — Allocated Cost
              </span>
              <span className="text-sm font-bold text-[#0F172A]">{fmt(totalAlloc)}</span>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function WaveCard({
  idx, wave, readOnly, total, allTypes, remainingByType, networkTotalByType,
  pgMeta, monthsFromWeeks, fmt, assignmentsGetter,
  onMoveUp, onMoveDown, onSplit, onMerge, onRemove, onRename,
  onPhaseGroup, onAddAssignment, onRemoveAssignment, onChangeCount,
}) {
  const assignments = assignmentsGetter ? assignmentsGetter(wave) : (wave.scope_assignments || []);
  const assignedKeys = new Set(assignments.map((a) => a.warehouse_type));
  const available = allTypes.filter((t) => !assignedKeys.has(t.key) && (remainingByType[t.key] || 0) > 0);
  const [picker, setPicker] = useState("");
  const linkedMeta = wave.phase_group_index != null && pgMeta[wave.phase_group_index]
    ? pgMeta[wave.phase_group_index]
    : null;
  const duration = linkedMeta ? linkedMeta.duration_weeks : Number(wave.duration_weeks || 0);

  return (
    <div
      data-testid={`impl-wave-${idx}`}
      className="rounded-sm border border-[#E5E7EB] bg-[#F8FAFC] px-3 py-2.5"
    >
      <div className="flex items-start gap-2 flex-wrap">
        {!readOnly && (
          <ArrowStack
            canUp={idx > 0}
            canDown={idx < total - 1}
            onUp={onMoveUp}
            onDown={onMoveDown}
            testid={`impl-wave-${idx}-move`}
          />
        )}
        <div className="min-w-0 flex-1">
          {readOnly ? (
            <p className="text-xs font-semibold text-[#111827]" data-testid={`impl-wave-${idx}-name`}>{wave.name}</p>
          ) : (
            <input
              type="text"
              data-testid={`impl-wave-${idx}-name`}
              value={wave.name || ""}
              onChange={(e) => onRename(e.target.value)}
              className="w-full text-xs font-semibold text-[#111827] bg-transparent border-b border-transparent hover:border-[#E5E7EB] focus:border-[#025979] focus:outline-none"
              placeholder={`Wave ${idx + 1}`}
            />
          )}

          {/* Linked phase group */}
          <div className="flex items-center gap-2 mt-1.5" data-testid={`impl-wave-${idx}-pg-row`}>
            <span className="text-[10px] uppercase tracking-wider text-[#6B7280]">Phase Group</span>
            {readOnly ? (
              <span className="text-[11px] font-medium text-[#111827]" data-testid={`impl-wave-${idx}-pg-label`}>
                {linkedMeta ? linkedMeta.name : "—"}
              </span>
            ) : (
              <select
                data-testid={`impl-wave-${idx}-pg-select`}
                value={wave.phase_group_index ?? ""}
                onChange={(e) => onPhaseGroup(e.target.value)}
                className="text-[11px] font-medium text-[#111827] bg-white border border-[#E5E7EB] rounded-sm px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-[#025979]/40"
              >
                <option value="">— none —</option>
                {pgMeta
                  .filter((m) => m.cls === "rollout")
                  .map((m) => (
                    <option key={m.index} value={m.index}>
                      {`${m.name} (${m.duration_weeks}w)`}
                    </option>
                  ))}
              </select>
            )}
            {linkedMeta && (
              <span className="text-[10px] text-[#6B7280]" data-testid={`impl-wave-${idx}-pg-duration`}>
                → {linkedMeta.duration_weeks} weeks
              </span>
            )}
          </div>

          {wave.description && (
            <p className="text-[11px] text-[#6B7280] mt-1" data-testid={`impl-wave-${idx}-desc`}>{wave.description}</p>
          )}

          {/* Scope helper + chips */}
          <p className="text-[10px] text-[#6B7280] mt-2" data-testid={`impl-wave-${idx}-scope-help`}>
            Assign warehouse sites to this wave. Use the count input on each chip to split a type across multiple waves (e.g., <em>Manufacturing 1 of 3</em>).
          </p>
          <div className="flex flex-wrap gap-1.5 mt-1 items-center" data-testid={`impl-wave-${idx}-scope`}>
            {assignments.length === 0 && !readOnly ? (
              <span className="text-[10px] text-[#9CA3AF] italic">No sites assigned — add on the right</span>
            ) : (
              assignments.map((a) => {
                const tObj = allTypes.find((t) => t.key === a.warehouse_type) || { label: a.warehouse_type, count: 0 };
                const total = tObj.count || 0;
                const lc = Number(a.location_count) || 0;
                return (
                  <span
                    key={a.warehouse_type}
                    data-testid={`impl-wave-${idx}-chip-${a.warehouse_type}`}
                    className="text-[10px] px-1.5 py-0.5 bg-[#E0F2FE] text-[#025979] rounded-sm border border-[#025979]/20 inline-flex items-center gap-1"
                  >
                    <span>{tObj.label}</span>
                    {readOnly ? (
                      <span className="text-[9px] px-1 py-px rounded-sm bg-white border border-[#025979]/20 text-[#025979]">
                        {lc} of {total}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-0.5">
                        <input
                          type="number"
                          min="1"
                          value={lc}
                          data-testid={`impl-wave-${idx}-chip-${a.warehouse_type}-count`}
                          onChange={(e) => onChangeCount(a.warehouse_type, e.target.value)}
                          className="w-10 text-[10px] px-1 py-px rounded-sm bg-white border border-[#025979]/20 text-[#025979] focus:outline-none focus:ring-1 focus:ring-[#025979]/40"
                          title={`Maximum ${total} — other waves' assignments are reserved`}
                        />
                        <span className="text-[9px] text-[#025979]">of {total}</span>
                      </span>
                    )}
                    {!readOnly && (
                      <button
                        type="button"
                        data-testid={`impl-wave-${idx}-chip-${a.warehouse_type}-remove`}
                        onClick={() => onRemoveAssignment(a.warehouse_type)}
                        aria-label={`Remove ${tObj.label}`}
                        className="text-[#025979] hover:text-[#C2410C]"
                      >
                        ×
                      </button>
                    )}
                  </span>
                );
              })
            )}
            {!readOnly && available.length > 0 && (
              <span className="inline-flex items-center gap-1">
                <select
                  data-testid={`impl-wave-${idx}-addtype-select`}
                  value={picker}
                  onChange={(e) => setPicker(e.target.value)}
                  className="text-[10px] border border-[#E5E7EB] bg-white rounded-sm px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-[#025979]/40"
                >
                  <option value="">Add type…</option>
                  {available.map((t) => (
                    <option key={t.key} value={t.key}>{`${t.label} (${remainingByType[t.key] || 0} of ${networkTotalByType[t.key] || 0} left)`}</option>
                  ))}
                </select>
                <button
                  type="button"
                  data-testid={`impl-wave-${idx}-addtype-confirm`}
                  onClick={() => { if (picker) { onAddAssignment(picker, 1); setPicker(""); } }}
                  disabled={!picker}
                  className="text-[10px] px-1.5 py-0.5 rounded-sm border border-[#025979] text-[#025979] hover:bg-[#E6F1F5] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Plus className="w-3 h-3 inline" />
                </button>
              </span>
            )}
          </div>
        </div>
        <div className="text-right text-[10px] text-[#6B7280] uppercase tracking-wider shrink-0">
          <p data-testid={`impl-wave-${idx}-timing`}>
            Week {wave.start_week ?? 0}–{wave.end_week ?? (Number(wave.start_week || 0) + duration)} · {monthsFromWeeks(duration)} mo
          </p>
          <p className="mt-0.5" data-testid={`impl-wave-${idx}-golive`}>
            Go-live month <span className="text-[#025979] font-semibold">{wave.go_live_month || "—"}</span>
          </p>
        </div>
      </div>
      {/* Metrics row */}
      <div className="grid grid-cols-4 gap-3 mt-2 pt-2 border-t border-[#E5E7EB]">
        <div data-testid={`impl-wave-${idx}-duration`}>
          <p className="text-[9px] text-[#9CA3AF] uppercase tracking-wider">Duration (weeks)</p>
          <p className="text-xs font-semibold text-[#111827]" title="Derived from selected Phase Group. Edit phase weeks in the Phase Groups editor below.">
            {duration} <span className="text-[9px] text-[#9CA3AF] normal-case font-normal">from Phase Group</span>
          </p>
        </div>
        <div data-testid={`impl-wave-${idx}-locations`}>
          <p className="text-[9px] text-[#9CA3AF] uppercase tracking-wider">Sites</p>
          <p className="text-xs font-semibold text-[#111827]">{wave.location_count ?? "—"}</p>
        </div>
        <div data-testid={`impl-wave-${idx}-effort-share`}>
          <p className="text-[9px] text-[#9CA3AF] uppercase tracking-wider">Effort Share</p>
          <p className="text-xs font-semibold text-[#111827]">
            {Number((wave.effort_share || 0) * 100).toFixed(1)}%
          </p>
        </div>
        <div data-testid={`impl-wave-${idx}-allocated`}>
          <p className="text-[9px] text-[#9CA3AF] uppercase tracking-wider">Allocated Cost</p>
          <p className="text-xs font-semibold text-[#025979]">{fmt(wave.allocated_one_time)}</p>
        </div>
      </div>
      {/* Row actions */}
      {!readOnly && (
        <div className="flex items-center gap-2 mt-2 pt-2 border-t border-dashed border-[#E5E7EB]">
          <button
            type="button"
            data-testid={`impl-wave-${idx}-split`}
            onClick={onSplit}
            title="Splits this wave into two waves so you can move some warehouse types or sites to a later rollout."
            className="text-[10px] px-2 py-1 rounded-sm border border-[#E5E7EB] text-[#374151] hover:bg-[#F3F4F6]"
          >
            Split into new wave
          </button>
          <button
            type="button"
            data-testid={`impl-wave-${idx}-merge`}
            onClick={onMerge}
            disabled={idx >= total - 1}
            title="Combines this wave with the next wave into one rollout wave."
            className="text-[10px] px-2 py-1 rounded-sm border border-[#E5E7EB] text-[#374151] hover:bg-[#F3F4F6] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Merge with next wave
          </button>
          <button
            type="button"
            data-testid={`impl-wave-${idx}-remove`}
            onClick={onRemove}
            disabled={total <= 1}
            className="text-[10px] px-2 py-1 rounded-sm border border-[#FCA5A5] text-[#B91C1C] hover:bg-[#FEE2E2] disabled:opacity-40 disabled:cursor-not-allowed ml-auto"
          >
            <Trash2 className="w-3 h-3 inline" /> Remove
          </button>
        </div>
      )}
    </div>
  );
}


// -----------------------------------------------------------------------------
// Phase 2: Cost Model section
// -----------------------------------------------------------------------------
// -----------------------------------------------------------------------------
// CostInput — hoisted (iter 59). Local-state + onBlur commit so the cursor
// doesn't lose focus on every keystroke as the parent re-renders.
// -----------------------------------------------------------------------------
function CostInput({ bucket, kKey, label, tid, hint, value, currency, onCommit }) {
  const [local, setLocal] = useState(String(value ?? 0));
  const [focused, setFocused] = useState(false);
  // Sync from parent when not actively editing (e.g., after external save).
  useEffect(() => {
    if (!focused) setLocal(String(value ?? 0));
  }, [value, focused]);
  const commit = () => {
    const n = local === "" ? 0 : Number(local);
    const finalVal = Number.isFinite(n) ? Math.max(0, n) : 0;
    setLocal(String(finalVal));
    onCommit(bucket, kKey, finalVal);
  };
  return (
    <div>
      <label className="block text-[10px] text-[#6B7280] uppercase tracking-wider mb-1">
        {label}
      </label>
      <div className="flex items-stretch border border-[#D1D5DB] rounded-sm overflow-hidden bg-white">
        <span className="px-2 text-xs text-[#9CA3AF] flex items-center bg-[#F9FAFB] border-r border-[#E5E7EB]">
          {currency}
        </span>
        <input
          type="number"
          step="1"
          data-testid={tid}
          value={local}
          onFocus={() => setFocused(true)}
          onChange={(e) => setLocal(e.target.value)}
          onBlur={() => { setFocused(false); commit(); }}
          onKeyDown={(e) => { if (e.key === "Enter") { e.currentTarget.blur(); } }}
          className="flex-1 px-2 py-1.5 text-sm focus:outline-none"
        />
      </div>
      {hint && <p className="text-[11px] text-[#9CA3AF] mt-1">{hint}</p>}
    </div>
  );
}

function CostModelSection({ costBreakdown, computedLaborCost, onUpdate }) {
  const cb = costBreakdown || { one_time: {}, recurring: {}, currency: "USD" };
  const ot = cb.one_time || {};
  const rc = cb.recurring || {};
  const overridden = !!ot.implementation_fees_overridden;
  // Effective fee — when not overridden, the field tracks the live labor
  // roll-up so phase/resource matrix edits flow straight through.
  const computedLabor = Number(computedLaborCost || 0);
  const effectiveImplFees = overridden
    ? Number(ot.implementation_fees || 0)
    : computedLabor;
  const impl_fees = effectiveImplFees;
  const hw = Number(ot.hardware_devices || 0);
  const ic = Number(ot.integration_custom_dev || 0);
  const ib = Number(ot.internal_business_effort || 0);
  const totalOneTime = impl_fees + hw + ic + ib;
  const sapLic = Number(rc.sap_license_subscription_delta || 0);
  const ams = Number(rc.annual_support_ams || 0);
  const totalRec = sapLic + ams;
  const currency = cb.currency || "USD";

  const patch = (bucket, key, v) => {
    const n = v === "" ? 0 : Number(v);
    onUpdate({
      ...cb,
      [bucket]: { ...(cb[bucket] || {}), [key]: Number.isFinite(n) ? Math.max(0, n) : 0 },
    });
  };

  // Patch implementation_fees specifically: typing a value flips the
  // override flag on; the "Sync" link flips it off. We treat typing a
  // value equal to (or within $1 of) the computed labor as "not really an
  // override" so consultants aren't surprised by the flag flipping when
  // they happen to re-type the displayed auto-tracked number.
  const patchImplFees = (_bucket, _key, v) => {
    const n = v === "" ? 0 : Number(v);
    const finalVal = Number.isFinite(n) ? Math.max(0, n) : 0;
    const isOverride = Math.abs(finalVal - computedLabor) > 1;
    onUpdate({
      ...cb,
      one_time: {
        ...(cb.one_time || {}),
        implementation_fees: finalVal,
        implementation_fees_overridden: isOverride,
      },
    });
  };
  const syncImplFees = () => {
    onUpdate({
      ...cb,
      one_time: {
        ...(cb.one_time || {}),
        implementation_fees: computedLabor,
        implementation_fees_overridden: false,
      },
    });
  };

  return (
    <section
      data-testid="impl-cost-model-section"
      className="rounded-sm border border-[#E5E7EB] p-4 bg-white"
    >
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-xs font-semibold text-[#025979] uppercase tracking-wider">
          Cost Model
        </h3>
        <p className="text-[11px] text-[#6B7280]">
          One-time drives <strong>Total Investment</strong> for ROI. Recurring is shown separately.
        </p>
      </div>

      {/* One-time */}
      <div className="mb-4">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-[#0F172A] mb-2">One-time Costs</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <CostInput
            bucket="one_time"
            kKey="implementation_fees"
            label="PrimeS4 Implementation Fees"
            tid="cost-implementation-fees"
            value={effectiveImplFees}
            currency={currency}
            onCommit={patchImplFees}
            hint={
              overridden
                ? (
                  <span data-testid="cost-implementation-fees-hint">
                    Override of computed labor ({currency} {computedLabor.toLocaleString()}).{" "}
                    <button
                      type="button"
                      data-testid="cost-implementation-fees-sync"
                      onClick={syncImplFees}
                      className="text-[11px] font-medium text-[#025979] hover:text-[#03738C] underline underline-offset-2"
                    >
                      Sync to Computed Labor
                    </button>
                  </span>
                )
                : (
                  <span data-testid="cost-implementation-fees-hint">
                    Auto-tracking computed labor ({currency} {computedLabor.toLocaleString()}). Type a value to override.
                  </span>
                )
            }
          />
          <CostInput
            bucket="one_time"
            kKey="hardware_devices"
            label="Hardware & Devices"
            tid="cost-hardware-devices"
            value={ot.hardware_devices ?? 0}
            currency={currency}
            onCommit={patch}
          />
          <CostInput
            bucket="one_time"
            kKey="integration_custom_dev"
            label="Integration / Custom Development"
            tid="cost-integration-custom-dev"
            value={ot.integration_custom_dev ?? 0}
            currency={currency}
            onCommit={patch}
          />
          <CostInput
            bucket="one_time"
            kKey="internal_business_effort"
            label="Internal Business Effort (estimated)"
            tid="cost-internal-business-effort"
            value={ot.internal_business_effort ?? 0}
            currency={currency}
            onCommit={patch}
          />
        </div>
        <div
          data-testid="cost-total-one-time"
          className="mt-3 flex items-baseline justify-between rounded-sm bg-[#E0F2FE] px-3 py-2 border border-[#025979]/20"
        >
          <span className="text-[10px] uppercase tracking-wider text-[#025979] font-semibold">
            Total One-Time Investment
          </span>
          <span className="text-base font-bold text-[#0F172A]">
            {currency} {totalOneTime.toLocaleString()}
          </span>
        </div>
      </div>

      {/* Recurring */}
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wider text-[#0F172A] mb-2">Annual Recurring Costs</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <CostInput
            bucket="recurring"
            kKey="sap_license_subscription_delta"
            label="SAP EWM License / Subscription Delta"
            tid="cost-sap-license"
            value={rc.sap_license_subscription_delta ?? 0}
            currency={currency}
            onCommit={patch}
          />
          <CostInput
            bucket="recurring"
            kKey="annual_support_ams"
            label="Annual Support / AMS"
            tid="cost-annual-support-ams"
            value={rc.annual_support_ams ?? 0}
            currency={currency}
            onCommit={patch}
          />
        </div>
        <div
          data-testid="cost-total-recurring"
          className="mt-3 flex items-baseline justify-between rounded-sm bg-[#F8FAFC] px-3 py-2 border border-[#E5E7EB]"
        >
          <span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">
            Total Annual Recurring
          </span>
          <span className="text-base font-bold text-[#0F172A]">
            {currency} {totalRec.toLocaleString()}
          </span>
        </div>
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Phase 2: Implementation & Investment Summary (final feed-into-ROI block)
// -----------------------------------------------------------------------------
function InvestmentSummarySection({
  core, costBreakdown,
  currency, exchangeRate, discountPct,
  onCurrency, onExchangeRate, onDiscount,
}) {
  const cb = costBreakdown || { one_time: {}, recurring: {}, currency: "USD" };
  const displayCurrency = currency || cb.currency || "USD";
  const ot = cb.one_time || {};
  const rc = cb.recurring || {};
  // Mirror CostModelSection: auto-track computed labor unless explicitly
  // overridden by the consultant.
  const overridden = !!ot.implementation_fees_overridden;
  const effectiveImplFees = overridden
    ? Number(ot.implementation_fees || 0)
    : Number(core?.totalCost || 0);
  const totalOneTime =
    effectiveImplFees +
    (Number(ot.hardware_devices || 0)) +
    (Number(ot.integration_custom_dev || 0)) +
    (Number(ot.internal_business_effort || 0));
  const totalRec =
    (Number(rc.sap_license_subscription_delta || 0)) +
    (Number(rc.annual_support_ams || 0));
  const months = core.totalWeeks > 0 ? (core.totalWeeks / 4.345).toFixed(1) : "0";
  const avgFte = core.totalWeeks > 0 ? (core.totalHours / (core.totalWeeks * 40)).toFixed(1) : "0";
  const fx = Number(exchangeRate) > 0 ? Number(exchangeRate) : 1;
  const disc = Math.max(0, Number(discountPct) || 0);
  const appliedOneTime = totalOneTime * fx * (1 - disc / 100);
  const appliedRec = totalRec * fx * (1 - disc / 100);
  const cell = (label, value, tid, sub) => (
    <div className="rounded-sm border border-[#E5E7EB] bg-white px-3 py-2" data-testid={tid}>
      <p className="text-[10px] uppercase tracking-wider text-[#6B7280]">{label}</p>
      <p className="text-base font-bold text-[#0F172A] mt-0.5">{value}</p>
      {sub && <p className="text-[10px] text-[#6B7280] mt-0.5">{sub}</p>}
    </div>
  );
  const fmt = (n) => `${displayCurrency} ${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  const hasFx = Math.abs(fx - 1) > 1e-9;
  const hasDisc = disc > 0;
  return (
    <section
      data-testid="impl-investment-summary"
      className="rounded-sm border border-[#025979]/30 bg-[#F0F9FF] p-4"
    >
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-xs font-semibold text-[#025979] uppercase tracking-wider" style={{ fontFamily: "Chivo" }}>
              Implementation &amp; Investment Summary
            </h3>
            <span
              data-testid="impl-summary-in-sync"
              className="inline-flex items-center gap-1 text-[10px] text-[#065F46] bg-[#ECFDF5] border border-[#065F46]/20 px-1.5 py-0.5 rounded-sm"
              title="Shares currency, exchange rate, and cost inputs with the ROI tab."
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#16A34A]" /> In sync with ROI
            </span>
          </div>
          <p className="text-[10.5px] text-[#025979]/80 mt-0.5">
            Feeds directly into the ROI tab. Discount only reduces the Implementation Total — ROI figures are unaffected.
          </p>
        </div>
        {/* Inline Currency / FX / Discount — top-right */}
        <div
          data-testid="impl-summary-fx-strip"
          className="flex items-center gap-1 text-[11px] text-[#0F172A]"
        >
          <div className="flex items-center gap-1 bg-white border border-[#E5E7EB] rounded-sm px-2 py-1">
            <span className="text-[10px] uppercase tracking-wider text-[#6B7280]">CCY</span>
            <Input
              data-testid="impl-currency-input"
              type="text"
              value={displayCurrency}
              placeholder="USD"
              onChange={(e) => onCurrency && onCurrency(e.target.value)}
              className="rounded-sm text-[11px] h-6 w-14 border-0 shadow-none p-0 focus-visible:ring-0"
            />
          </div>
          <span className="text-[#9CA3AF]">|</span>
          <div className="flex items-center gap-1 bg-white border border-[#E5E7EB] rounded-sm px-2 py-1">
            <span className="text-[10px] uppercase tracking-wider text-[#6B7280]">FX</span>
            <Input
              data-testid="impl-exchange-rate-input"
              type="number"
              step="0.0001"
              value={fx}
              onChange={(e) => onExchangeRate && onExchangeRate(e.target.value)}
              className="rounded-sm text-[11px] h-6 w-16 border-0 shadow-none p-0 focus-visible:ring-0"
            />
          </div>
          <span className="text-[#9CA3AF]">|</span>
          <div className="flex items-center gap-1 bg-white border border-[#E5E7EB] rounded-sm px-2 py-1">
            <span className="text-[10px] uppercase tracking-wider text-[#6B7280]">DISC</span>
            <Input
              data-testid="impl-discount-input"
              type="number"
              step="0.1"
              min="0"
              value={disc}
              onChange={(e) => onDiscount && onDiscount(e.target.value)}
              className="rounded-sm text-[11px] h-6 w-12 border-0 shadow-none p-0 focus-visible:ring-0"
            />
            <span className="text-[10px] text-[#6B7280]">%</span>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {cell("Total Duration", `${core.totalWeeks || 0} weeks`, "impl-summary-duration", `${months} months`)}
        {cell("Total Effort", `${Number(core.totalHours || 0).toLocaleString()} hrs`, "impl-summary-effort", "Across all phases")}
        {cell("Avg. Team Size", `${avgFte} FTE`, "impl-summary-team", "Full-time equivalents")}
        {cell(
          "Total One-Time Investment",
          fmt(appliedOneTime),
          "impl-summary-one-time",
          (hasFx || hasDisc)
            ? `Base ${fmt(totalOneTime)}${hasFx ? ` · FX ${fx}` : ""}${hasDisc ? ` · -${disc}%` : ""}`
            : "Before tax; consultant-entered"
        )}
        {cell(
          "Total Annual Recurring",
          fmt(appliedRec),
          "impl-summary-recurring",
          (hasFx || hasDisc)
            ? `Base ${fmt(totalRec)}${hasFx ? ` · FX ${fx}` : ""}${hasDisc ? ` · -${disc}%` : ""}`
            : "License + AMS"
        )}
      </div>
    </section>
  );
}
